import React from 'react';
import LibraryTableHead from '../components/common/LibraryTableHead';
import DateFilterInput from '../components/form/DateFilterInput';
import { useShowPeriodSummaries } from '../hooks/useShowPeriodSummaries';
import '../components/event/ShowPeriodStatus.css';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRoles } from '../hooks/useRoles';
import useSessionStore from '../state/session';
import useActiveLocationStore from '../state/locationScope';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { useFilterParams } from '../hooks/useFilterParams';
import { useOrderStageReference } from '../hooks/useOrderStageReference';
import { useOrderStageGroups } from '../hooks/useOrderStageGroups';
import { deriveOrderStage, formatOrderStatusLabel } from '../lib/orderStages';
import CreateEventWizard from './event/CreateEventWizard';
import BEOImportReview from '../components/event/BEOImportReview';
import BEOUploadModal from '../components/event/BEOUploadModal';
import type { ParsedBEOPacket } from '../hooks/useBEOScan';
import { useAIFeatures } from '../hooks/useAISettings';
import { useBEOImportJobs } from '../hooks/useBEOImportJobs';
import { useEventCustomerTotalSummary, useEventsList, useLocations, useOrderDocSequences, usePmSpOptions, useEventDashboardCounts } from '../domain/events/useEvents';
import EventLeaderboardModal from '../components/event/EventLeaderboardModal';
import {
  isEventAvailabilityRelevant,
  useEventsAvailabilityMap,
} from '../domain/events/useEventAvailability';
import type { EventAvailability } from '../domain/events/useEventAvailability';
import { useEventsOverdueMap } from '../data/overdue';
import EventFlags from '../components/EventFlags';
import { eventCustomerTotalAmount, type EventListRow, type EventListStatusFilter, type EventCustomerTotalSummaryFilters } from '../data/events';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import { safeErrorMessage } from '../utils/appErrors';
import { GuideQuestionButton, GuideTourOverlay, useGuideTour } from '../components/guides/GuideTour';
import { EVENTS_LIBRARY_GUIDE } from '../components/guides/pageGuideContent';
import { useEventMutations } from '../hooks/mutations/useEventMutations';
import { useInvalidateAvailability } from '../hooks/mutations/useInventoryMutations';
import EventActionsDropdown from '../components/event/EventActionsDropdown';
import type { EventStageUpdatePayload } from '../components/event/EventActionsDropdown';
import StageFilterToolbar from '../components/events/StageFilterToolbar';
import SearchableMultiSelect, { type MultiSelectOption } from '../components/SearchableMultiSelect';
import { formatUserSummary, useUserIdMap } from '../hooks/useUserIdMap';
import EventsCalendarView from '../components/events/EventsCalendarView';
import { LibraryAdvancedToggle } from '../ui/LibraryAdvancedToggle';
import { LibraryFilterToggle } from '../ui/LibraryFilterToggle';
import { LibraryViewToggle } from '../ui/LibraryViewToggle';
import LibrarySearchInput from '../components/common/LibrarySearchInput';
import LibraryFilterSheet, { LibraryFilterSheetField, LibraryFilterSheetGroup } from '../components/common/LibraryFilterSheet';
import useCompactToolbar from '../hooks/useCompactToolbar';
import useLibraryPhoneLayout from '../hooks/useLibraryPhoneLayout';
import LibraryRecordCard, {
  LibraryRecordCardDetail,
  LibraryRecordCardList,
  LibraryRecordCardSkeleton,
} from '../components/common/LibraryRecordCard';
import LibraryPager from '../components/common/LibraryPager';
import { LocationPinIcon } from '../ui/LocationPinIcon';
import '../components/status/StatusChip.css';
import LibrarySavedViews from '../components/common/LibrarySavedViews';
import { useSavedViews } from '../hooks/useSavedViews';
import { useToasts } from '../ui/ToastProvider';
import { fetchBeoImportJob } from '../data/beoImportJobs';
import { formatDateOnly } from '@/domain/dates';
import { readPriceCheckEventDraft, removePriceCheckEventDraft } from '@/domain/events/priceCheckDraft';
import { resolveSelectedStageStatuses, stageStatusesToFilterValues } from '@/domain/events/stageFilterState';
import { resolveConfirmedPipelineStatuses, summarizeEventHeaderRows } from '@/domain/events/eventHeaderStats';
import { eventDisplayName } from '@/domain/events';
import type { LibraryPreview } from '../components/library/LibraryPreviewPanel';
import { useShellLayout } from '../ui/ShellLayoutContext';
import LayoutFeature from '../ui/LayoutFeature';
import {
  ALL_SITE_FILTER,
  buildSiteFilterOptions,
  normalizeSiteFilterChange,
  normalizeSiteFilterValues,
  readSessionFilterState,
  resolveSiteFilterLocationIds,
  sanitizeSiteFilterValues,
  siteFilterStorageValue,
  summarizeSiteFilterSelection,
  writeSessionFilterState,
} from '@/domain/library';

type EventRow = EventListRow;

export function initialEventsCalendarRange(now = dayjs()) {
  const month = now.startOf('day');
  return {
    start: month.startOf('month').startOf('week').format('YYYY-MM-DD'),
    end: month.endOf('month').endOf('week').format('YYYY-MM-DD'),
  };
}

export type ListSortKey = 'latest' | 'stage' | 'r2' | 'name' | 'date' | 'endDate' | 'createdDate' | 'venue' | 'netAmount' | 'items' | 'customer' | 'pm' | 'sp';
export type ApiSortBy = 'latest' | 'r2' | 'name' | 'date' | 'endDate' | 'stage' | 'createdDate';

export type EventsViewConfig = {
  groupBy: 'none' | 'date' | 'venue' | 'customer' | 'pm' | 'sp';
  sortBy: ApiSortBy;
  listSortKey: ListSortKey;
  listSortDir: 'asc' | 'desc';
  siteFilter: string;
  siteFilters?: string[];
  stageFilters: string[];
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  viewMode?: 'grid' | 'list' | 'calendar';
  showCalendarWarehouseWhiskers?: boolean;
  pmFilter?: string[];
  spFilter?: string[];
  fulfillmentFilter?: 'all' | 'customer_pickup' | 'ita_delivery';
};

export type EventsSavedView = { id: string; name: string; config: EventsViewConfig };

/** Event order stage from events table only (events.status). */
function extractEventOrderStatuses(ev: EventRow): string[] {
  const s = typeof ev.status === 'string' ? ev.status.trim() : '';
  return s ? [s] : [];
}

function normalizeDate(value?: string | null): string{
  if(!value) return '';
  const trimmed = value.trim();
  if(!trimmed) return '';
  const [datePart] = trimmed.split('T');
  return datePart.length >= 10 ? datePart.slice(0, 10) : datePart;
}

function eventStartDateValue(ev: EventRow): string | null{
  return ev.event_start_date || ev.show_start_date || null;
}

function eventEndDateValue(ev: EventRow): string | null{
  return ev.event_end_date || ev.show_end_date || null;
}

// Stages to exclude from the default checked set on first load.
const EXCLUDED_STAGE_LABELS = new Set(['Lost', 'Cancelled', 'Invoiced', 'Closed']);
const DEFAULT_STAGE_EXCLUDED_STATUS_VALUES = new Set(['lost', 'cancelled', 'void', 'invoiced', 'closed']);
const CONFIRMED_TO_INVOICED_EXCLUDED_STATUS_VALUES = new Set(['lost', 'cancelled', 'void', 'closed']);
const CONFIRMED_TO_INVOICED_FALLBACK_STATUS_VALUES = [
  'confirmed',
  'picking',
  'partially_filled',
  'filled',
  'partially_shipped',
  'shipped',
  'on_rent',
  'partially_returned',
  'returning',
  'returned',
  'ready_to_invoice',
  'invoiced',
];
const NO_STAGE_MATCH_STATUS = '__stage_filter_no_match__';
const EVENTS_SITE_FILTER_SESSION_KEY = 'events.siteFilters.session';

function areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
  if (left.size !== right.size) return false;
  for (const item of left) {
    if (!right.has(item)) return false;
  }
  return true;
}

function normalizeViewConfigStringList(values: string[] | null | undefined): string[] {
  const next: string[] = [];
  for (const value of values ?? []) {
    const normalized = String(value ?? '').trim();
    if (normalized && !next.includes(normalized)) next.push(normalized);
  }
  return next.sort((a, b) => a.localeCompare(b));
}

function normalizeEventsViewConfigForComparison(config: EventsViewConfig | null | undefined): EventsViewConfig {
  const siteFilters = normalizeViewConfigStringList(
    normalizeSiteFilterValues(config?.siteFilters ?? config?.siteFilter),
  );
  const normalizedSiteFilters = siteFilters.includes(ALL_SITE_FILTER) ? [ALL_SITE_FILTER] : siteFilters;
  return {
    groupBy: config?.groupBy ?? 'none',
    sortBy: config?.sortBy ?? 'date',
    listSortKey: config?.listSortKey ?? 'date',
    listSortDir: config?.listSortDir === 'desc' ? 'desc' : 'asc',
    siteFilter: siteFilterStorageValue(normalizedSiteFilters),
    siteFilters: normalizedSiteFilters,
    stageFilters: normalizeViewConfigStringList(config?.stageFilters),
    startDateFrom: config?.startDateFrom ?? '',
    startDateTo: config?.startDateTo ?? '',
    endDateFrom: config?.endDateFrom ?? '',
    endDateTo: config?.endDateTo ?? '',
    viewMode: config?.viewMode ?? 'calendar',
    showCalendarWarehouseWhiskers: config?.showCalendarWarehouseWhiskers !== false,
    pmFilter: normalizeViewConfigStringList(config?.pmFilter),
    spFilter: normalizeViewConfigStringList(config?.spFilter),
    fulfillmentFilter: config?.fulfillmentFilter ?? 'all',
  };
}

function areEventsViewConfigsEqual(
  left: EventsViewConfig | null | undefined,
  right: EventsViewConfig | null | undefined,
): boolean {
  return JSON.stringify(normalizeEventsViewConfigForComparison(left)) ===
    JSON.stringify(normalizeEventsViewConfigForComparison(right));
}

function readSessionSiteFilterValues(profileId: string | null | undefined): string[] | null {
  const parsed = readSessionFilterState<string[]>(EVENTS_SITE_FILTER_SESSION_KEY, profileId);
  return Array.isArray(parsed) ? normalizeSiteFilterValues(parsed) : null;
}

function writeSessionSiteFilterValues(profileId: string | null | undefined, values: string[]): void {
  writeSessionFilterState(
    EVENTS_SITE_FILTER_SESSION_KEY,
    profileId,
    normalizeSiteFilterValues(values),
  );
}

function resolveDefaultSiteFilterValues(
  homeLocationId: string | null | undefined,
  optionIds: Set<string>,
  canSelectAllSites: boolean,
  regionOptions: Array<{ region_id: string }>,
  siteOptions: Array<{ location_id: string }>,
): string[] {
  if (homeLocationId && optionIds.has(homeLocationId)) {
    return [homeLocationId];
  }
  if (!canSelectAllSites) {
    const firstRegion = regionOptions[0]?.region_id ? `region:${regionOptions[0].region_id}` : null;
    if (firstRegion && optionIds.has(firstRegion)) return [firstRegion];
    const firstSite = siteOptions[0]?.location_id ?? null;
    if (firstSite && optionIds.has(firstSite)) return [firstSite];
  }
  return [ALL_SITE_FILTER];
}


/** Group and Sort choices, shared by the toolbar and the phone filter sheet. */
const EVENT_GROUP_OPTIONS: ReadonlyArray<{ value: 'none' | 'date' | 'venue' | 'customer' | 'pm' | 'sp'; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'date', label: 'Date' },
  { value: 'venue', label: 'Venue' },
  { value: 'customer', label: 'Customer' },
  { value: 'pm', label: 'Project manager' },
  { value: 'sp', label: 'Sales person' },
];

const EVENT_SORT_OPTIONS: ReadonlyArray<{ value: ListSortKey; label: string }> = [
  { value: 'latest', label: 'Latest' },
  { value: 'stage', label: 'Order stage' },
  { value: 'r2', label: 'R2 #' },
  { value: 'name', label: 'Name' },
  { value: 'date', label: 'Start date' },
  { value: 'endDate', label: 'End date' },
  { value: 'createdDate', label: 'Created date' },
  { value: 'venue', label: 'Venue' },
  { value: 'netAmount', label: 'Customer total' },
  { value: 'items', label: 'Items' },
  { value: 'customer', label: 'Customer' },
  { value: 'pm', label: 'Project manager' },
  { value: 'sp', label: 'Sales person' },
];

/** List, Calendar and Grid; phones drop Grid, whose cards would match List's. */
const VIEW_OPTIONS: ReadonlyArray<{ value: 'grid' | 'list' | 'calendar'; label: string }> = [
  { value: 'list', label: 'List' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'grid', label: 'Grid' },
];
const PHONE_VIEW_OPTIONS = VIEW_OPTIONS.filter((option) => option.value !== 'grid');
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export default function EventsPage() {
  const { isManager, isAdmin } = useRoles();
  const { push } = useToasts();
  const navigate = useNavigate();
  const profileId = useSessionStore((s) => s.profileId);
  const profile = useSessionStore((s) => s.profile);
  const sessionReady = useSessionStore((s) => s.sessionReady);
  const allowedLocationIds = useSessionStore((s) => s.allowedLocationIds);
  const assignedRegions = useSessionStore((s) => s.assignedRegions);
  const locationInitialised = useActiveLocationStore((state) => state.initialised);
  const setActiveLocationId = useActiveLocationStore((state) => state.setActiveLocationId);
  const setActiveRegionId = useActiveLocationStore((state) => state.setActiveRegionId);
  const allowedLocationsKey = React.useMemo(() => (allowedLocationIds || []).filter(Boolean).join(','), [allowedLocationIds]);
  const locationsQuery = useLocations({ allowedLocationIds, enabled: locationInitialised });
  const siteOptions = React.useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const siteOptionsLoading = locationsQuery.isLoading;
  const siteOptionsError = locationsQuery.error
    ? safeErrorMessage(locationsQuery.error, 'Locations could not be loaded.')
    : null;
  const profileHomeLocationId = profile?.home_location_id ?? null;

  // Build region options with their constituent location IDs for region-level filtering.
  // Only show regions where the user has grant_all_locations access.
  const regionFilterOptions = React.useMemo(() => {
    if (!assignedRegions.length || !siteOptions.length) return [];
    return assignedRegions
      .filter((r) => r.grant_all_locations && r.is_active && r.name)
      .map((r) => ({
        region_id: r.region_id,
        name: r.name!,
        locationIds: siteOptions
          .filter((loc) => loc.region_id === r.region_id)
          .map((loc) => loc.location_id),
      }))
      .filter((r) => r.locationIds.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedRegions, siteOptions]);

  const docSequenceLocationIds = React.useMemo(
    () => siteOptions.map((opt) => opt.location_id).filter(Boolean),
    [siteOptions],
  );
  const docSequencesQuery = useOrderDocSequences({
    locationIds: docSequenceLocationIds,
    enabled: docSequenceLocationIds.length > 0,
  });
  const docSequences = docSequencesQuery.data ?? [];
  const siteOrderPrefixes = React.useMemo(() => {
    const next: Record<string, string[]> = {};
    for (const row of docSequences) {
      const locId = row.location_id || '';
      const docType = typeof row.doc_type === 'string' ? row.doc_type.trim().toUpperCase() : '';
      const prefix = typeof row.prefix === 'string' ? row.prefix.trim() : '';
      if (!locId || !prefix || docType !== 'ORDER') continue;
      if (!next[locId]) next[locId] = [];
      const normalized = prefix.toUpperCase();
      if (!next[locId].includes(normalized)) next[locId].push(normalized);
    }
    return next;
  }, [docSequences]);
  const { softDelete } = useEventMutations();
  const invalidateAvailability = useInvalidateAvailability();
  const [showWizard, setShowWizard] = React.useState(false);
  const [showLeaderboard, setShowLeaderboard] = React.useState(false);
  const eventsActionsGuideRef = React.useRef<HTMLDivElement | null>(null);
  const eventsToolbarGuideRef = React.useRef<HTMLDivElement | null>(null);
  const eventsResultsGuideRef = React.useRef<HTMLDivElement | null>(null);
  // BEO import state
  const { beoImportEnabled } = useAIFeatures();
  const { addJob: addBeoJob } = useBEOImportJobs();
  const [beoData, setBeoData] = React.useState<ParsedBEOPacket | null>(null);
  const [showBeoUploadModal, setShowBeoUploadModal] = React.useState(false);
  const dashboardCountsQuery = useEventDashboardCounts();
  const dashboardCounts = dashboardCountsQuery.data;
  const location = useLocation();
  const { get, set: setPref, loaded: prefsLoaded, flush } = useUserPrefs();
  const hasSiteFilterUrlParam = React.useMemo(() => {
    try {
      return new URLSearchParams(location.search).has('site');
    } catch {
      return false;
    }
  }, [location.search]);

  const savedViews = React.useMemo(() => {
    const raw = get<EventsSavedView[]>('events.savedViews', []);
    return Array.isArray(raw) ? raw : [];
  }, [get]);
  const defaultViewId = get<string | null>('events.defaultViewId', null);
  // The default view ("loads on open") decides what the page opens with; otherwise the person's own
  // saved filters do. Neither is written back: only the controls save (docs/ui-preferences.md).
  const defaultViewConfig = React.useMemo(
    () => (defaultViewId ? savedViews.find((view) => view.id === defaultViewId)?.config ?? null : null),
    [defaultViewId, savedViews],
  );

  // ── URL-driven filter state via useFilterParams ──
  // Read on every render: a URL param wins, else the saved value. The defaults follow the saved
  // preferences once they load, so a return visit in the same tab shows them too.
  const filterSchema = React.useMemo(() => {
    const view = defaultViewConfig;
    const list = (value: unknown): string[] => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []);
    return {
      q: { default: '' },
      view: { default: view ? view.viewMode ?? 'calendar' : get<string>('events.viewMode', 'calendar') },
      group: { default: view ? view.groupBy ?? 'none' : get<string>('events.groupBy', 'none') },
      sort: { default: view ? view.sortBy ?? 'date' : get<string>('events.sortBy', 'date') },
      sk: { default: view ? view.listSortKey ?? 'date' : get<string>('events.listSort.key', 'date') },
      sd: { default: view ? view.listSortDir ?? 'asc' : get<string>('events.listSort.dir', 'asc') },
      site: { type: 'string[]' as const, default: [ALL_SITE_FILTER] },
      // An empty list shows the default stages (resolveSelectedStageStatuses).
      stages: { type: 'string[]' as const, default: list(view ? view.stageFilters : get<string[]>('events.stageFilters', [])) },
      sf: { default: view ? view.startDateFrom ?? '' : get<string>('events.startDateFrom', '') },
      st: { default: view ? view.startDateTo ?? '' : get<string>('events.startDateTo', '') },
      ef: { default: view ? view.endDateFrom ?? '' : get<string>('events.endDateFrom', '') },
      et: { default: view ? view.endDateTo ?? '' : get<string>('events.endDateTo', '') },
      pm: { type: 'string[]' as const, default: list(view ? view.pmFilter : get<string[]>('events.pmFilter', [])) },
      sp: { type: 'string[]' as const, default: list(view ? view.spFilter : get<string[]>('events.spFilter', [])) },
      avail: { default: (() => { const stored = get<string>('events.availabilityMode', ''); if (['glow','bar','icon','off'].includes(stored)) return stored; const oldGlow = get<boolean>('events.availabilityGlow', true); return oldGlow !== false ? 'glow' : 'off'; })() },
      fulfillment: { default: view?.fulfillmentFilter ?? 'all' },
    };
  }, [defaultViewConfig, get]);
  const { values: filters, set: setFilter, setMany: setFiltersMany } = useFilterParams(filterSchema);

  // Derived filter values and setter wrappers that maintain the existing API
  const [search, setSearch] = React.useState(filters.q);
  const debouncedSearch = filters.q;
  const viewMode = filters.view as 'grid' | 'list' | 'calendar';
  const setViewMode = React.useCallback((v: 'grid' | 'list' | 'calendar') => { setFilter('view', v); setPref('events.viewMode', v); }, [setFilter, setPref]);
  const groupBy = filters.group as 'none' | 'date' | 'venue' | 'customer' | 'pm' | 'sp';
  const setGroupBy = React.useCallback((v: typeof groupBy) => { setFilter('group', v); setPref('events.groupBy', v); }, [setFilter, setPref]);
  const sortBy = filters.sort as ApiSortBy;
  const setSortBy = React.useCallback((v: ApiSortBy) => { setFilter('sort', v); setPref('events.sortBy', v); }, [setFilter, setPref]);
  const listSortKey = filters.sk as ListSortKey;
  const setListSortKey = React.useCallback((v: ListSortKey) => { setFilter('sk', v); setPref('events.listSort.key', v); }, [setFilter, setPref]);
  const listSortDir = (filters.sd === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';
  const setListSortDir = React.useCallback((v: 'asc' | 'desc') => { setFilter('sd', v); setPref('events.listSort.dir', v); }, [setFilter, setPref]);
  const siteFilters = React.useMemo(() => normalizeSiteFilterValues(filters.site), [filters.site]);
  const setSiteFilters = React.useCallback((values: string[], rememberInSession = true) => {
    const normalized = normalizeSiteFilterValues(values);
    setFilter('site', normalized);
    if (rememberInSession) {
      writeSessionSiteFilterValues(profileId, normalized);
    }
  }, [profileId, setFilter]);
  const startDateFrom = filters.sf;
  const setStartDateFrom = React.useCallback((v: string) => { setFilter('sf', v); setPref('events.startDateFrom', v); }, [setFilter, setPref]);
  const startDateTo = filters.st;
  const setStartDateTo = React.useCallback((v: string) => { setFilter('st', v); setPref('events.startDateTo', v); }, [setFilter, setPref]);
  const endDateFrom = filters.ef;
  const setEndDateFrom = React.useCallback((v: string) => { setFilter('ef', v); setPref('events.endDateFrom', v); }, [setFilter, setPref]);
  const endDateTo = filters.et;
  const setEndDateTo = React.useCallback((v: string) => { setFilter('et', v); setPref('events.endDateTo', v); }, [setFilter, setPref]);
  const pmFilter = filters.pm;
  const setPmFilter = React.useCallback((v: string[]) => { setFilter('pm', v); setPref('events.pmFilter', v); }, [setFilter, setPref]);
  const spFilter = filters.sp;
  const setSpFilter = React.useCallback((v: string[]) => { setFilter('sp', v); setPref('events.spFilter', v); }, [setFilter, setPref]);
  const availabilityMode = filters.avail as 'off' | 'glow' | 'bar' | 'icon';
  const setAvailabilityMode = React.useCallback((v: 'off' | 'glow' | 'bar' | 'icon') => { setFilter('avail', v); setPref('events.availabilityMode', v); }, [setFilter, setPref]);
  const fulfillmentFilter = filters.fulfillment as 'all' | 'customer_pickup' | 'ita_delivery';
  const setFulfillmentFilter = React.useCallback((value: 'all' | 'customer_pickup' | 'ita_delivery') => {
    setFilter('fulfillment', value);
  }, [setFilter]);
  // null until the person (or a saved view they apply) sets it on this visit.
  const [whiskersChoice, setWhiskersChoice] = React.useState<boolean | null>(null);
  const showCalendarWarehouseWhiskers = whiskersChoice ?? (
    typeof defaultViewConfig?.showCalendarWarehouseWhiskers === 'boolean'
      ? defaultViewConfig.showCalendarWarehouseWhiskers
      : get<boolean>('events.calendar.showWarehouseWhiskers', true) !== false
  );
  const setShowCalendarWarehouseWhiskers = React.useCallback((checked: boolean) => {
    setWhiskersChoice(checked);
    setPref('events.calendar.showWarehouseWhiskers', checked);
    flush();
  }, [flush, setPref]);
  const canRevealHiddenCalendarEvents = isManager || isAdmin;
  const [showHiddenCalendarEvents, setShowHiddenCalendarEvents] = React.useState(false);
  const stageFilters = React.useMemo(() => new Set(filters.stages), [filters.stages]);
  const stageFiltersRef = React.useRef<Set<string>>(new Set(filters.stages));
  React.useEffect(() => {
    stageFiltersRef.current = new Set(filters.stages);
  }, [filters.stages]);
  React.useEffect(() => {
    if (!canRevealHiddenCalendarEvents && showHiddenCalendarEvents) {
      setShowHiddenCalendarEvents(false);
    }
  }, [canRevealHiddenCalendarEvents, showHiddenCalendarEvents]);
  const setStageFilters = React.useCallback((v: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    if (typeof v === 'function') {
      const current = new Set(stageFiltersRef.current);
      const next = v(current);
      stageFiltersRef.current = new Set(next);
      const arr = Array.from(next);
      setFilter('stages', arr);
      setPref('events.stageFilters', arr);
    } else {
      const arr = Array.from(v);
      stageFiltersRef.current = new Set(v);
      setFilter('stages', arr);
      setPref('events.stageFilters', arr);
    }
  }, [setFilter, setPref]);

  const siteFilterRef = React.useRef<string[]>([ALL_SITE_FILTER]);
  const siteFilterInitialisedRef = React.useRef(false);
  const [siteFilterReady, setSiteFilterReady] = React.useState(false);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  // Phones (≤720px): Filters opens the shared filter sheet instead of an inline panel.
  const compactToolbar = useCompactToolbar();
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false);
  const filterToggleRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (!compactToolbar) setFilterSheetOpen(false);
  }, [compactToolbar]);
  // Phones (≤640px) show one record card per event in List (Grid would be the same cards, so
  // phones offer List and Calendar only).
  const isPhone = useLibraryPhoneLayout();
  // Seed the same visible month window that EventsCalendarView reports after
  // mount. Waiting solely on the child effect can leave the initial calendar
  // query permanently disabled until the user changes views.
  const [calendarRange, setCalendarRange] = React.useState<{ start: string; end: string }>(
    initialEventsCalendarRange,
  );
  const handleCalendarDateRangeChange = React.useCallback((start: string, end: string) => {
    setCalendarRange({ start, end });
  }, []);
  const pmSpQuery = usePmSpOptions();
  const pmOptions = React.useMemo(
    () => (pmSpQuery.data?.pmOptions ?? []).map((o) => ({ id: o.id, label: o.name })),
    [pmSpQuery.data],
  );
  const spOptions = React.useMemo(
    () => (pmSpQuery.data?.spOptions ?? []).map((o) => ({ id: o.id, label: o.name })),
    [pmSpQuery.data],
  );
  const canSelectAllSites = isManager || isAdmin;
  const siteFilterOptions: MultiSelectOption[] = React.useMemo(
    () => buildSiteFilterOptions({
      regions: regionFilterOptions,
      sites: siteOptions,
      canSelectAllSites,
      allLabel: 'All sites',
    }),
    [canSelectAllSites, regionFilterOptions, siteOptions],
  );
  const siteFilterSummary = React.useMemo(
    () => summarizeSiteFilterSelection(siteFilters, siteFilterOptions, 'All sites'),
    [siteFilterOptions, siteFilters],
  );
  // The Filters badge counts a site choice only when it differs from the default (the
  // viewer's home location, else their first region or site, else all sites).
  const defaultSiteFilters = React.useMemo(() => resolveDefaultSiteFilterValues(
    profileHomeLocationId,
    new Set(siteFilterOptions.map((option) => option.id)),
    canSelectAllSites,
    regionFilterOptions,
    siteOptions,
  ), [canSelectAllSites, profileHomeLocationId, regionFilterOptions, siteFilterOptions, siteOptions]);
  const siteFilterChanged = siteFilters.length !== defaultSiteFilters.length
    || siteFilters.some((value) => !defaultSiteFilters.includes(value));
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };
  // The phone pager sits under the last card; the next page starts at the top.
  const goToPhonePage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(() => new Set());
  const applyActiveSiteScope = React.useCallback((values: string[]) => {
    const normalized = normalizeSiteFilterValues(values);
    if (normalized.length !== 1) {
      setActiveLocationId(null);
      return;
    }
    const value = normalized[0];
    if (value.startsWith('region:')) {
      setActiveRegionId(value.slice(7));
    } else {
      setActiveLocationId(value === ALL_SITE_FILTER ? null : value);
    }
  }, [setActiveLocationId, setActiveRegionId]);
  React.useEffect(() => {
    if (siteFilterInitialisedRef.current) return;
    if (!sessionReady || !locationInitialised || siteOptionsLoading) return;

    const optionIds = new Set(siteFilterOptions.map((option) => option.id));
    const urlSiteFilters = hasSiteFilterUrlParam
      ? sanitizeSiteFilterValues(siteFilters, optionIds, canSelectAllSites)
      : null;
    const sessionSiteFilters = sanitizeSiteFilterValues(
      readSessionSiteFilterValues(profileId),
      optionIds,
      canSelectAllSites,
    );
    const nextSiteFilters =
      urlSiteFilters ??
      sessionSiteFilters ??
      resolveDefaultSiteFilterValues(
        profileHomeLocationId,
        optionIds,
        canSelectAllSites,
        regionFilterOptions,
        siteOptions,
      );
    const siteScopeAlreadyApplied = nextSiteFilters.length === siteFilters.length
      && nextSiteFilters.every((value, index) => value === siteFilters[index]);

    siteFilterInitialisedRef.current = true;
    siteFilterRef.current = nextSiteFilters;
    setSiteFilters(nextSiteFilters);
    applyActiveSiteScope(nextSiteFilters);
    // Writing the same URL-backed filter is a React Router no-op. In that case
    // no render would wake the follow-up effect below, leaving all event queries
    // disabled until some unrelated interaction (such as changing views).
    if (siteScopeAlreadyApplied) setSiteFilterReady(true);
  }, [
    applyActiveSiteScope,
    canSelectAllSites,
    hasSiteFilterUrlParam,
    locationInitialised,
    profileHomeLocationId,
    profileId,
    regionFilterOptions,
    sessionReady,
    setSiteFilters,
    siteFilterOptions,
    siteFilters,
    siteOptions,
    siteOptionsLoading,
  ]);
  React.useEffect(() => {
    if (!siteFilterInitialisedRef.current || siteFilterReady) return;
    const pendingSiteFilters = normalizeSiteFilterValues(siteFilterRef.current);
    if (
      pendingSiteFilters.length !== siteFilters.length
      || pendingSiteFilters.some((value, index) => value !== siteFilters[index])
    ) {
      return;
    }
    // useFilterParams writes through React Router. Wait until that URL-backed
    // state reflects the resolved home/session scope before enabling queries;
    // otherwise a stale default "all sites" request can race the intended one.
    setSiteFilterReady(true);
  }, [siteFilterReady, siteFilters]);
  const applyViewConfig = React.useCallback((config: EventsViewConfig) => {
    const nextSiteFilters = normalizeSiteFilterValues(config.siteFilters ?? config.siteFilter);
    setViewMode(config.viewMode ?? 'calendar');
    setGroupBy(config.groupBy);
    setSortBy(config.sortBy);
    setListSortKey(config.listSortKey);
    setListSortDir(config.listSortDir);
    setSiteFilters(nextSiteFilters, true);
    siteFilterRef.current = nextSiteFilters;
    applyActiveSiteScope(nextSiteFilters);
    setStageFilters(new Set(Array.isArray(config.stageFilters) ? config.stageFilters : []));
    setStartDateFrom(config.startDateFrom ?? '');
    setStartDateTo(config.startDateTo ?? '');
    setEndDateFrom(config.endDateFrom ?? '');
    setEndDateTo(config.endDateTo ?? '');
    if (typeof config.showCalendarWarehouseWhiskers === 'boolean') {
      setShowCalendarWarehouseWhiskers(config.showCalendarWarehouseWhiskers);
    }
    setPmFilter(Array.isArray(config.pmFilter) ? config.pmFilter : []);
    setSpFilter(Array.isArray(config.spFilter) ? config.spFilter : []);
    setFulfillmentFilter(config.fulfillmentFilter ?? 'all');
  }, [applyActiveSiteScope, setViewMode, setGroupBy, setSortBy, setListSortKey, setListSortDir, setSiteFilters, setStageFilters, setStartDateFrom, setStartDateTo, setEndDateFrom, setEndDateTo, setShowCalendarWarehouseWhiskers, setPmFilter, setSpFilter, setFulfillmentFilter]);
  const currentViewConfig = React.useMemo((): EventsViewConfig => ({
    groupBy,
    sortBy,
    listSortKey,
    listSortDir,
    siteFilter: siteFilterStorageValue(siteFilters),
    siteFilters,
    stageFilters: Array.from(stageFilters),
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    viewMode,
    showCalendarWarehouseWhiskers,
    pmFilter,
    spFilter,
    fulfillmentFilter,
  }), [groupBy, sortBy, listSortKey, listSortDir, siteFilters, stageFilters, startDateFrom, startDateTo, endDateFrom, endDateTo, viewMode, showCalendarWarehouseWhiskers, pmFilter, spFilter, fulfillmentFilter]);
  const savedViewPrefs = React.useMemo(
    () => ({ get, set: setPref, flush }),
    [flush, get, setPref],
  );
  const savedViewsController = useSavedViews<EventsViewConfig>({
    prefs: savedViewPrefs,
    viewsKey: 'events.savedViews',
    defaultKey: 'events.defaultViewId',
    current: currentViewConfig,
    apply: applyViewConfig,
    equals: areEventsViewConfigsEqual,
  });
  const { setAppliedId: setAppliedSavedViewId } = savedViewsController;
  const toggleGroupCollapsed = React.useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const {
    lookup: orderStageLookup,
    loading: orderStageReferenceLoading,
  } = useOrderStageReference();
  const { groups: stageGroups, standaloneStatuses } = useOrderStageGroups();
  const orderedStatuses = React.useMemo(
    () =>
      Object.entries(orderStageLookup)
        .sort((a, b) => a[1].stageIndex - b[1].stageIndex)
        .map(([status]) => ({
          status,
          label: orderStageLookup[status]?.label ?? formatOrderStatusLabel(status),
        })),
    [orderStageLookup],
  );
  const statusToStageLabel = React.useMemo(() => {
    const next = new Map<string, string>();
    for (const option of orderedStatuses) {
      next.set(option.status, option.label);
    }
    return next;
  }, [orderedStatuses]);
  const stageLabelToStatuses = React.useMemo(() => {
    const next = new Map<string, string[]>();
    for (const option of orderedStatuses) {
      const statuses = next.get(option.label) ?? [];
      statuses.push(option.status);
      next.set(option.label, statuses);
    }
    return next;
  }, [orderedStatuses]);
  const fallbackStageStatuses = React.useMemo(
    () => orderedStatuses.map((option) => option.status),
    [orderedStatuses],
  );
  const defaultStageStatuses = React.useMemo(
    () =>
      orderedStatuses
        .filter((option) => (
          !DEFAULT_STAGE_EXCLUDED_STATUS_VALUES.has(option.status)
          && !EXCLUDED_STAGE_LABELS.has(option.label)
        ))
        .map((option) => option.status),
    [orderedStatuses],
  );
  const confirmedPipelineStatuses = React.useMemo(() => {
    return resolveConfirmedPipelineStatuses(orderedStatuses);
  }, [orderedStatuses]);
  const calendarConfirmedToInvoicedStatuses = React.useMemo(() => {
    const normalized = orderedStatuses.map((option) => ({
      status: option.status,
      key: option.status.trim().toLowerCase(),
    }));
    const confirmedIndex = normalized.findIndex((option) => option.key === 'confirmed');
    const invoicedIndex = normalized.findIndex((option) => option.key === 'invoiced');
    const sourceStatuses = confirmedIndex >= 0
      ? normalized
        .slice(
          confirmedIndex,
          invoicedIndex >= confirmedIndex ? invoicedIndex + 1 : undefined,
        )
        .map((option) => option.status)
      : CONFIRMED_TO_INVOICED_FALLBACK_STATUS_VALUES;
    return sourceStatuses.filter((status) => {
      const key = status.trim().toLowerCase();
      return key && !CONFIRMED_TO_INVOICED_EXCLUDED_STATUS_VALUES.has(key);
    });
  }, [orderedStatuses]);
  const selectedStageStatuses = React.useMemo(() => {
    return resolveSelectedStageStatuses(stageFilters, stageLabelToStatuses, defaultStageStatuses);
  }, [defaultStageStatuses, stageFilters, stageLabelToStatuses]);
  const defaultStageStatusSet = React.useMemo(() => new Set(defaultStageStatuses), [defaultStageStatuses]);
  const stageFilterActive = defaultStageStatusSet.size > 0
    ? !areSetsEqual(selectedStageStatuses, defaultStageStatusSet)
    : stageFilters.size > 0;

  const [filterOverdue, setFilterOverdue] = React.useState(false);
  const [filterUrgent, setFilterUrgent] = React.useState(false);

  // Resolve API status filter from stage checkboxes so filtering is over all matching events
  const statusFilter = React.useMemo((): EventListStatusFilter => {
    if (orderedStatuses.length === 0) return 'all';
    const statuses = Array.from(selectedStageStatuses);
    return { in: statuses.length > 0 ? statuses : [NO_STAGE_MATCH_STATUS] };
  }, [orderedStatuses.length, selectedStageStatuses]);

  // List and card groups share normal server paging. A larger one-shot request
  // silently hits the Data API row cap and makes later events unreachable.
  const CALENDAR_PAGE_SIZE = 2000;

  // Resolve selected sites and regions to an array of event.site_location_id values.
  const resolvedSiteLocationIds = React.useMemo(
    () => resolveSiteFilterLocationIds(siteFilters, regionFilterOptions),
    [siteFilters, regionFilterOptions],
  );

  // Resolve API sort from list sort key (server-backed keys drive API; others keep current sortBy)
  const SERVER_SORT_KEYS: ListSortKey[] = ['latest', 'stage', 'r2', 'name', 'date', 'endDate', 'createdDate'];
  const apiSortByFromListKey: ApiSortBy = SERVER_SORT_KEYS.includes(listSortKey) ? (listSortKey as ApiSortBy) : sortBy;

  // Reset to page 1 when search, filters, or sort order change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, groupBy, sortBy, listSortKey, listSortDir, siteFilters, startDateFrom, startDateTo, endDateFrom, endDateTo, stageFilters, pmFilter, spFilter, fulfillmentFilter, filterOverdue, filterUrgent, showCalendarWarehouseWhiskers, showHiddenCalendarEvents]);

  // Calendar view scopes results to the visible date window and sorts by date ascending
  const isCalendar = viewMode === 'calendar';
  const calendarVisibility = isCalendar && canRevealHiddenCalendarEvents && showHiddenCalendarEvents ? 'all' : 'visible';
  const calendarConfirmedTotalStatusFilter = React.useMemo((): EventListStatusFilter => ({
    in: calendarConfirmedToInvoicedStatuses.length > 0
      ? calendarConfirmedToInvoicedStatuses
      : [NO_STAGE_MATCH_STATUS],
  }), [calendarConfirmedToInvoicedStatuses]);
  const confirmedResultStatusFilter = React.useMemo((): EventListStatusFilter => {
    const selected = statusFilter === 'all' ? [...confirmedPipelineStatuses]
      : statusFilter === 'none' ? []
      : statusFilter.in.filter(status => confirmedPipelineStatuses.has(status.trim().toLowerCase()));
    return { in: selected };
  }, [confirmedPipelineStatuses, statusFilter]);
  const resultFilters: EventCustomerTotalSummaryFilters = {
    search: debouncedSearch,
    siteLocationIds: resolvedSiteLocationIds,
    statusFilter,
    startDateFrom: isCalendar ? undefined : (startDateFrom || undefined),
    startDateTo: isCalendar ? undefined : (startDateTo || undefined),
    endDateFrom: isCalendar ? undefined : (endDateFrom || undefined),
    endDateTo: isCalendar ? undefined : (endDateTo || undefined),
    calendarWindowStart: isCalendar && calendarRange ? calendarRange.start : undefined,
    calendarWindowEnd: isCalendar && calendarRange ? calendarRange.end : undefined,
    includeWarehouseTasksInCalendar: showCalendarWarehouseWhiskers,
    calendarVisibility,
    pmProfileIds: pmFilter.length > 0 ? pmFilter : undefined,
    spAuthIds: spFilter.length > 0 ? spFilter : undefined,
    customerPickup: fulfillmentFilter === 'all' ? undefined : fulfillmentFilter === 'customer_pickup',
    overdueOnly: filterOverdue,
    urgentOnly: filterUrgent,
  };
  const eventsQuery = useEventsList({
    // The default status set is built from event_status_reference. Starting
    // before it loads issues an unfiltered request and then immediately repeats
    // the same search with the real status predicate.
    enabled: (
      siteFilterReady
      && !orderStageReferenceLoading
      && (!isCalendar || Boolean(calendarRange))
    ),
    filters: {
      ...resultFilters,
      includeFlagCounts: true,
      sortBy: isCalendar ? 'date' : apiSortByFromListKey,
      sortDir: isCalendar ? 'asc' : listSortDir,
      offset: isCalendar ? 0 : (currentPage - 1) * pageSize,
      pageSize: isCalendar ? CALENDAR_PAGE_SIZE : pageSize,
      skipTotals: false,
    },
  });
  const filteredConfirmedTotalQuery = useEventCustomerTotalSummary({
    enabled: !isCalendar && siteFilterReady && !orderStageReferenceLoading && eventsQuery.isSuccess,
    filters: { ...resultFilters, statusFilter: confirmedResultStatusFilter },
  });
  // Calendar rows are the primary content. Only start header totals and the
  // manager-only hidden-row count after the bounded row query succeeds, so
  // three copies of the wide calendar overlap predicate cannot race the Data
  // API statement timeout and leave the calendar looking empty.
  const calendarConfirmedTotalQuery = useEventCustomerTotalSummary({
    enabled: (
      isCalendar
      && eventsQuery.isSuccess
      && siteFilterReady
      && Boolean(calendarRange)
      && calendarConfirmedToInvoicedStatuses.length > 0
    ),
    filters: {
      siteLocationIds: resolvedSiteLocationIds,
      statusFilter: calendarConfirmedTotalStatusFilter,
      calendarWindowStart: calendarRange?.start,
      calendarWindowEnd: calendarRange?.end,
      includeWarehouseTasksInCalendar: showCalendarWarehouseWhiskers,
      calendarVisibility,
      customerPickup: fulfillmentFilter === 'all' ? undefined : fulfillmentFilter === 'customer_pickup',
    },
  });
  const hiddenCalendarEventsQuery = useEventsList({
    enabled: (
      isCalendar
      && eventsQuery.isSuccess
      && canRevealHiddenCalendarEvents
      && siteFilterReady
      && !orderStageReferenceLoading
      && Boolean(calendarRange)
    ),
    filters: {
      search: debouncedSearch,
      sortBy: 'date',
      sortDir: 'asc',
      offset: 0,
      pageSize: 1,
      siteLocationIds: resolvedSiteLocationIds,
      statusFilter,
      calendarWindowStart: calendarRange?.start,
      calendarWindowEnd: calendarRange?.end,
      includeWarehouseTasksInCalendar: showCalendarWarehouseWhiskers,
      calendarVisibility: 'hidden',
      pmProfileIds: pmFilter.length > 0 ? pmFilter : undefined,
      spAuthIds: spFilter.length > 0 ? spFilter : undefined,
      customerPickup: fulfillmentFilter === 'all' ? undefined : fulfillmentFilter === 'customer_pickup',
      overdueOnly: filterOverdue,
      urgentOnly: filterUrgent,
      skipTotals: true,
    },
  });
  const EMPTY_EVENTS_RESULT = React.useRef({ data: [] as EventListRow[], count: null as number | null }).current;
  const eventsResult = eventsQuery.data ?? EMPTY_EVENTS_RESULT;
  const hiddenCalendarEventCount = hiddenCalendarEventsQuery.data?.count ?? 0;
  const [optimisticEventStatuses, setOptimisticEventStatuses] = React.useState<Record<string, string>>({});
  const eventsWithOptimisticStatus = React.useMemo(() => {
    if (!eventsResult.data.length) return eventsResult.data;
    return eventsResult.data.map((ev) => {
      const optimisticStatus = optimisticEventStatuses[ev.id];
      if (!optimisticStatus || optimisticStatus === ev.status) return ev;
      return { ...ev, status: optimisticStatus };
    });
  }, [eventsResult.data, optimisticEventStatuses]);
  React.useEffect(() => {
    if (!Object.keys(optimisticEventStatuses).length) return;
    const serverStatusById = new Map<string, string>();
    for (const ev of eventsResult.data) {
      serverStatusById.set(ev.id, ev.status ?? '');
    }
    setOptimisticEventStatuses((prev) => {
      let changed = false;
      const next: Record<string, string> = {};
      for (const [eventId, optimisticStatus] of Object.entries(prev)) {
        const serverStatus = serverStatusById.get(eventId);
        if (!serverStatus) {
          changed = true;
          continue;
        }
        if ((serverStatus ?? '') === optimisticStatus) {
          changed = true;
          continue;
        }
        next[eventId] = optimisticStatus;
      }
      return changed ? next : prev;
    });
  }, [eventsResult.data, optimisticEventStatuses]);
  const allEvents = eventsWithOptimisticStatus;
  const totalCount = eventsResult.count ?? 0;
  const loading = eventsQuery.isLoading; // avoid empty-state flash on first load
  const refreshing = eventsQuery.isFetching && !eventsQuery.isLoading;
  const error = eventsQuery.error
    ? safeErrorMessage(eventsQuery.error, 'Events could not be loaded.')
    : null;

  const salesManagerAuthIds = React.useMemo(
    () => allEvents.map((ev) => ev.sales_manager_id).filter((id): id is string => Boolean(id)),
    [allEvents],
  );
  const salesManagerNameMap = useUserIdMap(salesManagerAuthIds, { refreshTrigger: allEvents });

  const orderStatusOptions = React.useMemo(() => {
    const statuses = new Set<string>();
    for(const ev of allEvents){
      for(const status of extractEventOrderStatuses(ev)){ statuses.add(status); }
    }
    return Array.from(statuses).sort((a,b)=> formatOrderStatusLabel(a).localeCompare(formatOrderStatusLabel(b)));
  }, [allEvents]);

  const setStageFilterStatuses = React.useCallback((statuses: string[]) => {
    setStageFilters(new Set(stageStatusesToFilterValues(statuses, statusToStageLabel)));
  }, [setStageFilters, statusToStageLabel]);

  const handleSiteFiltersChange = React.useCallback((values: string[]) => {
    const nextSiteFilters = normalizeSiteFilterChange(values, siteFilters);
    siteFilterRef.current = nextSiteFilters;
    setSiteFilters(nextSiteFilters, true);
    applyActiveSiteScope(nextSiteFilters);
  }, [applyActiveSiteScope, setSiteFilters, siteFilters]);

  const resetAdvancedFilters = React.useCallback(() => {
    setStageFilterStatuses(defaultStageStatuses);
    setStartDateFrom('');
    setStartDateTo('');
    setEndDateFrom('');
    setEndDateTo('');
    setPmFilter([]);
    setSpFilter([]);
    setFulfillmentFilter('all');
  }, [defaultStageStatuses, setStageFilterStatuses, setStartDateFrom, setStartDateTo, setEndDateFrom, setEndDateTo, setPmFilter, setSpFilter, setFulfillmentFilter]);
  // Reset in the phone filter sheet: everything its badge counts.
  const resetFrequentFilters = React.useCallback(() => {
    setGroupBy('none');
    handleSiteFiltersChange(defaultSiteFilters);
    setStageFilterStatuses(defaultStageStatuses);
    setFilterOverdue(false);
    setFilterUrgent(false);
  }, [defaultSiteFilters, defaultStageStatuses, handleSiteFiltersChange, setGroupBy, setStageFilterStatuses]);
  const handleGroupByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupBy(event.target.value as typeof groupBy);
  };
  const handleListSortKeyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const key = event.target.value as ListSortKey;
    setListSortKey(key);
    setListSortDir(key === 'latest' ? 'desc' : 'asc');
    if (SERVER_SORT_KEYS.includes(key)) setSortBy(key as ApiSortBy);
  };
  const toggleListSortDir = () => setListSortDir(listSortDir === 'asc' ? 'desc' : 'asc');
  const eventsGuide = useGuideTour({
    steps: EVENTS_LIBRARY_GUIDE.steps,
    prefPath: EVENTS_LIBRARY_GUIDE.prefPath,
    storageKey: EVENTS_LIBRARY_GUIDE.storageKey,
    version: EVENTS_LIBRARY_GUIDE.version,
    enabled: sessionReady,
    ready: sessionReady,
    resolveTarget: React.useCallback((target: string) => {
      if (target === 'actions') return eventsActionsGuideRef.current;
      if (target === 'toolbar') return eventsToolbarGuideRef.current;
      if (target === 'results') return eventsResultsGuideRef.current;
      return null;
    }, []),
  });

  React.useEffect(() => {
    siteFilterRef.current = siteFilters;
  }, [siteFilters]);

  // Guard: if user role cannot see "All sites", default to first region or first location
  React.useEffect(() => {
    if (isManager || isAdmin) return;
    if (!siteFilters.includes(ALL_SITE_FILTER)) return;
    if (siteOptionsLoading) return;
    const fallback =
      (regionFilterOptions.length > 0 ? `region:${regionFilterOptions[0].region_id}` : null) ??
      siteOptions[0]?.location_id ??
      null;
    if (fallback) {
      const nextSiteFilters = normalizeSiteFilterValues([fallback]);
      siteFilterRef.current = nextSiteFilters;
      setSiteFilters(nextSiteFilters);
      applyActiveSiteScope(nextSiteFilters);
    }
  }, [applyActiveSiteScope, isManager, isAdmin, siteFilters, siteOptionsLoading, regionFilterOptions, siteOptions, setSiteFilters]);

  // Debounce search to reduce refetch churn
  React.useEffect(() => {
    const t = setTimeout(() => setFilter('q', search), 250);
    return () => clearTimeout(t);
  }, [search, setFilter]);

  // Searching from the calendar switches to list view so results are scannable.
  // Only fires when the applied term changes, so manually returning to the
  // calendar mid-search is respected; skips setPref so the saved default view
  // preference is untouched.
  const lastAppliedSearchRef = React.useRef(debouncedSearch);
  React.useEffect(() => {
    const prev = lastAppliedSearchRef.current;
    lastAppliedSearchRef.current = debouncedSearch;
    if (debouncedSearch === prev || !debouncedSearch.trim()) return;
    if (viewMode === 'calendar') setFilter('view', 'list');
  }, [debouncedSearch, viewMode, setFilter]);

  async function handleDeleteEvent(id: string, name?: string|null){
    if (!(isManager || isAdmin)) return;
    const label = name && String(name).trim() ? `\n\n${name}` : '';
    if (!confirm(`Delete this event? This is a soft delete and can be restored from History.${label}`)) return;
    try {
      await softDelete.mutateAsync(id);
      await eventsQuery.refetch();
    } catch (err: any) {
      alert(err?.message ?? 'Unable to delete event.');
    }
  }

  const handleStageUpdated = React.useCallback((payload?: EventStageUpdatePayload) => {
    if (payload?.eventId && payload.status) {
      setOptimisticEventStatuses((prev) => {
        if (prev[payload.eventId] === payload.status) return prev;
        return { ...prev, [payload.eventId]: payload.status };
      });
      const reservationStatus = String(payload.reservationJobStatus ?? '').toLowerCase();
      if (reservationStatus === 'pending' || reservationStatus === 'processing') {
        push({
          type: 'info',
          message: 'Event confirmed. Reservations are still being created in the background.',
        });
      }
    }
    invalidateAvailability();
    void eventsQuery.refetch();
  }, [eventsQuery, invalidateAvailability, push]);

  // Open create event modal when navigated with ?create=1 (e.g., from Home Quick Action or venue library "Create Event")
  const initialVenueIdFromUrl = React.useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search);
      if (qs.get('create') !== '1') return null;
      return qs.get('venueId') ?? null;
    } catch {
      return null;
    }
  }, [location.search]);

  const priceCheckDraftKeyFromUrl = React.useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search);
      if (qs.get('create') !== '1') return null;
      return qs.get('priceCheckDraft') ?? null;
    } catch {
      return null;
    }
  }, [location.search]);

  const initialPriceCheckDraftFromUrl = React.useMemo(
    () => readPriceCheckEventDraft(priceCheckDraftKeyFromUrl),
    [priceCheckDraftKeyFromUrl],
  );

  React.useEffect(() => {
    try {
      const qs = new URLSearchParams(location.search);
      if (qs.get('create') === '1') {
        setShowWizard(true);
      }
    } catch {}
  }, [location.search]);

  function onCreated(createdEvent?: { id?: string | null } | null) {
    if (priceCheckDraftKeyFromUrl) {
      removePriceCheckEventDraft(priceCheckDraftKeyFromUrl);
    }
    setShowWizard(false);
    const createdId = createdEvent?.id ? String(createdEvent.id).trim() : '';
    if (createdId) {
      // Open the new event straight away instead of dropping the user back on
      // the library to hunt for what they just created. Refetching the list is
      // pointless when we navigate away from it.
      navigate(`/events/${createdId}/overview`);
      return;
    }
    void eventsQuery.refetch();
  }

  // Handle ?beo_job=<jobId> URL param (from notification click)
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const beoJobId = params.get('beo_job');
    if (!beoJobId) return;
    // Remove the param from URL so it doesn't re-trigger on re-render
    params.delete('beo_job');
    const cleanSearch = params.toString();
    navigate({ pathname: location.pathname, search: cleanSearch ? `?${cleanSearch}` : '' }, { replace: true });
    // Fetch the job and open the review modal if done
    (async () => {
      const job = await fetchBeoImportJob(beoJobId);
      if (!job) {
        push({ type: 'error', message: 'BEO import job not found' });
        return;
      }
      if (job.status === 'done' && job.result_json) {
        const result = job.result_json as unknown as ParsedBEOPacket;
        if (Array.isArray(result.events)) {
          setBeoData(result);
        } else {
          push({ type: 'error', message: 'BEO import result has unexpected format' });
        }
      } else if (job.status === 'error') {
        push({ type: 'error', message: `BEO import failed: ${job.error_message || 'Unknown error'}` });
      } else {
        push({ type: 'info', message: 'BEO import is still processing — you\u2019ll be notified when ready.' });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // DEV ONLY: load test fixture for BEO import modal
  async function handleBeoTestFixture() {
    const { testBEOPacket } = await import('../components/event/__tests__/beoTestFixture');
    setBeoData(testBEOPacket);
  }

  // The default view is applied through the filter defaults above; mark it as the applied saved
  // view once preferences load, unless the URL already chose filters of its own.
  const defaultViewMarkedRef = React.useRef(false);
  React.useEffect(() => {
    if (!prefsLoaded || defaultViewMarkedRef.current) return;
    defaultViewMarkedRef.current = true;
    if (!defaultViewId || !defaultViewConfig) return;
    const params = new URLSearchParams(location.search);
    const explicit = Object.keys(filterSchema).some((key) => key !== 'q' && key !== 'site' && params.has(key));
    if (!explicit) setAppliedSavedViewId(defaultViewId);
  }, [defaultViewConfig, defaultViewId, filterSchema, location.search, prefsLoaded, setAppliedSavedViewId]);

  const serverSortKeys = SERVER_SORT_KEYS;

  function getValueForSort(ev: EventRow, key: ListSortKey): string | number {
    switch(key){
      case 'latest': return 0;
      case 'stage': return (orderStatusSummary(ev) || '').toString().toLowerCase();
      case 'r2': return (ev.event_code || ev.r2_contract_iid || '').toString().toLowerCase();
      case 'name': return (ev.name || '').toString().toLowerCase();
      case 'date': {
        const startKey = normalizeDate(eventStartDateValue(ev));
        if(!startKey) return -Infinity;
        const parsed = Date.parse(startKey);
        return Number.isNaN(parsed) ? -Infinity : parsed;
      }
      case 'endDate': {
        const endKey = normalizeDate(eventEndDateValue(ev));
        if(!endKey) return -Infinity;
        const parsed = Date.parse(endKey);
        return Number.isNaN(parsed) ? -Infinity : parsed;
      }
      case 'createdDate': {
        const ca = ev.created_at;
        if (!ca) return -Infinity;
        const parsed = Date.parse(ca);
        return Number.isNaN(parsed) ? -Infinity : parsed;
      }
      case 'venue': return (primaryVenueName(ev) || '').toString().toLowerCase();
      case 'netAmount': {
        const amount = eventCustomerTotalAmount(ev);
        return amount != null && Number.isFinite(amount) ? amount : -Infinity;
      }
      case 'items': return ev.item_count ?? 0;
      case 'customer': return (ev.client_display_name ?? ev.client_business ?? '').toString().toLowerCase();
      case 'pm': return (ev.project_manager_display_name ?? '').toString().toLowerCase();
      case 'sp': return (salesManagerDisplayName(ev) ?? '').toString().toLowerCase();
      default: return '';
    }
  }
  const sortRows = React.useCallback((rows: EventRow[]): EventRow[] => {
    // Preserve the complete server ordering, including null placement and the
    // stable ID tie-breaker, rather than sorting each page a second time.
    if (SERVER_SORT_KEYS.includes(listSortKey)) return rows;
    const dir = listSortDir === 'asc' ? 1 : -1;
    const key = listSortKey;
    const numericKeys: ListSortKey[] = ['date', 'endDate', 'createdDate', 'netAmount', 'items'];
    return [...rows].sort((a,b)=>{
      const av = getValueForSort(a, key);
      const bv = getValueForSort(b, key);
      if (numericKeys.includes(key)) {
        const na = typeof av === 'number' ? av : Number(av||0);
        const nb = typeof bv === 'number' ? bv : Number(bv||0);
        return (na - nb) * dir;
      }
      const sa = String(av||'');
      const sb = String(bv||'');
      if(sa < sb) return -1*dir;
      if(sa > sb) return 1*dir;
      return 0;
    });
  }, [listSortDir, listSortKey]);
  const handleHeaderSort = React.useCallback((nextKey: ListSortKey) => {
    if (listSortKey === nextKey) {
      setListSortDir(listSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setListSortKey(nextKey);
      setListSortDir('asc');
    }
    if (serverSortKeys.includes(nextKey)) setSortBy(nextKey as ApiSortBy);
  }, [listSortKey, listSortDir, setListSortKey, setListSortDir, setSortBy]);
  function sortIndicator(active: boolean){ return active ? (listSortDir==='asc' ? ' ▲' : ' ▼') : ''; }
  function ariaSort(col: ListSortKey): 'none'|'ascending'|'descending'|'other'{
    if(listSortKey!==col) return 'none';
    return listSortDir==='asc' ? 'ascending' : 'descending';
  }

  // Derived helpers
  function primaryVenueName(ev: EventRow): string | null {
    try { return ev.event_venues?.[0]?.venues?.venue_name || null; } catch { return null; }
  }

  function salesManagerDisplayName(ev: EventRow): string | null {
    return formatUserSummary(salesManagerNameMap, ev.sales_manager_id) ?? null;
  }

  function orderStatusSummary(ev: EventRow): string {
    const statuses = extractEventOrderStatuses(ev);
    if (statuses.length === 0) return '—';
    return statuses.map(formatOrderStatusLabel).join(', ');
  }

  function formatEventAmount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

/**
 * Availability ratio 0–1 → 3-color status:
 *   green  = no shortages (ratio === 1)
 *   yellow = shortages on <50% of items (ratio > 0.5)
 *   red    = shortages on 50%+ of items (ratio <= 0.5)
 */
function availabilityStatusFromRatio(ratio: number | undefined | null, returnWarning?: string | null): 'green' | 'yellow' | 'red' | 'unknown' {
  if (ratio == null || ratio < 0) return 'unknown';
    if (ratio >= 1) return returnWarning ? 'yellow' : 'green';
  if (ratio > 0.5) return 'yellow';
  return 'red';
}

/** Availability ratio 0–1 → tooltip title. */
  function availabilityTooltip(ratio: number | undefined | null, returnWarning?: string | null, timeError?: string | null): string {
  if (timeError) return timeError;
  if (ratio == null || ratio < 0) return 'Availability not calculated';
    if (ratio >= 1) return returnWarning ? `All items fit the forecast. ${returnWarning}` : 'All items available';
  if (ratio <= 0) return 'No items available';
  const pct = Math.round(ratio * 100);
  return `${pct}% of items available`;
}

/** Format short-count bar text: "3/25 (12%) Short" */
function shortageBarText(avail: EventAvailability | undefined): string {
  if (avail?.timeError) return 'Review time';
  if (!avail || avail.totalLines === 0) return '';
    if (avail.shortLines <= 0) return avail.returnWarning ? 'Check returns' : '';
  const pct = Math.round((avail.shortLines / avail.totalLines) * 100);
  return `${avail.shortLines}/${avail.totalLines} (${pct}%) Short`;
}

  function groupLabel(kind: typeof groupBy, ev: EventRow): string {
    if (kind === 'venue') return primaryVenueName(ev) || 'Unassigned venue';
    if (kind === 'customer') return (ev.client_display_name ?? ev.client_business) || 'Unassigned customer';
    if (kind === 'date') return formatDateOnly(eventStartDateValue(ev), { month:'long', year:'numeric' }) || 'No date';
    if (kind === 'pm') return ev.project_manager_display_name ?? 'Unassigned PM';
    if (kind === 'sp') return salesManagerDisplayName(ev) ?? 'Unassigned SP';
    return 'All events';
  }

  const events = React.useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    // In paginated mode (groupBy === 'none') the server already applies ILIKE
    // search filters, so skip the expensive client-side scan.
    if (!term || groupBy === 'none') return allEvents;
    return allEvents.filter((ev) => {
      const startDisplay = formatDateOnly(eventStartDateValue(ev));
      const endDisplay = formatDateOnly(eventEndDateValue(ev));
      const statusSummary = orderStatusSummary(ev);
      const venue = primaryVenueName(ev);
      const netStr = formatEventAmount(eventCustomerTotalAmount(ev));
      const customer = ev.client_display_name ?? ev.client_business ?? '';
      const pm = ev.project_manager_display_name ?? '';
      const sp = formatUserSummary(salesManagerNameMap, ev.sales_manager_id) ?? '';
      const searchStr = [
        ev.name ?? '',
        ev.event_code ?? '',
        ev.r2_contract_iid ?? '',
        startDisplay,
        endDisplay,
        statusSummary,
        venue ?? '',
        netStr,
        (ev.item_count ?? 0).toString(),
        customer,
        pm,
        sp,
      ].join(' ').toLowerCase();
      return searchStr.includes(term);
    });
  }, [allEvents, debouncedSearch, salesManagerNameMap, groupBy]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ── Only calculate availability for events the user can actually see ──
  // Skip events past on_rent / past their return date (availability is moot) and
  // events hidden inside collapsed groups. The hook schedules bounded chunks,
  // so large grouped/calendar views can publish indicators progressively.
  const visibleEventsForAvailability = React.useMemo(() => {
    let filtered = events.filter(event => isEventAvailabilityRelevant(event));

    // When grouping is active, also exclude events in collapsed groups
    if (groupBy !== 'none' && collapsedGroups.size > 0) {
      filtered = filtered.filter(ev => {
        const label = groupLabel(groupBy, ev);
        return !collapsedGroups.has(label);
      });
    }

    return filtered;
  }, [events, groupBy, collapsedGroups]);

  // Availability is evaluated per event in the database — batching events into
  // one RPC call saves round trips but not work, and the function itself is
  // documented (and guarded) for at most 50 events. Calendar views
  // load up to CALENDAR_PAGE_SIZE rows, so feeding the raw list here queued
  // thousands of events of work on every load, refetch, and window focus. Bound
  // it to the same visible window the overdue lookup already uses.
  const availabilityEvents = React.useMemo(
    () => visibleEventsForAvailability.slice(0, pageSize),
    [pageSize, visibleEventsForAvailability],
  );
  const availabilityMap = useEventsAvailabilityMap(availabilityEvents, { enabled: availabilityMode !== 'off' && availabilityEvents.length > 0 });
  const overdueEventIds = React.useMemo(
    () => availabilityEvents.map(event => event.id),
    [availabilityEvents],
  );
  const overdueMapQuery = useEventsOverdueMap(overdueEventIds);
  const EMPTY_OVERDUE_MAP = React.useRef<Map<string, import('../data/overdue').EventOverdueRow>>(new Map()).current;
  const overdueMap = overdueMapQuery.data ?? EMPTY_OVERDUE_MAP;
  const overdueCount = eventsQuery.data?.flagCounts ?? { overdue: null, urgent: null };
  const displayEvents = events;
  // Memoize the sorted display events so the sort isn't recomputed on every render
  const sortedDisplayEvents = React.useMemo(() => sortRows(displayEvents), [sortRows, displayEvents]);
  // A test layout's list preview (docs/test-layouts.md, "Page features"): built from the rows already loaded,
  // only while the layout has the feature. The panel holds the selection, so hovering never re-renders the page.
  const { features: layoutFeatures, kit: layoutKit } = useShellLayout();
  const ListPreviewPanel = layoutFeatures.listPreview && viewMode === 'list' && groupBy === 'none' ? layoutKit?.LibraryPreviewPanel : undefined;
  const listPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const eventPreviews = React.useMemo(() => {
    if (!ListPreviewPanel) return null;
    return new Map(sortedDisplayEvents.map((ev): [string, LibraryPreview] => {
      const code = ev.event_code || ev.r2_contract_iid || '';
      return [ev.id, {
        kicker: code ? `Event ${code}` : 'Event',
        title: eventDisplayName(ev.name, ev.customer_pickup) || 'Untitled Event',
        fields: [
          { label: 'Stage', value: orderStatusSummary(ev) || '—' },
          { label: 'Dates', value: [formatDateOnly(eventStartDateValue(ev)), formatDateOnly(eventEndDateValue(ev))].filter(Boolean).join(' – ') || '—' },
          { label: 'Venue', value: primaryVenueName(ev) || '—' },
          { label: 'Customer', value: ev.client_display_name ?? ev.client_business ?? '—' },
          { label: 'PM · SP', value: [ev.project_manager_display_name, salesManagerDisplayName(ev)].filter(Boolean).join(' · ') || '—' },
          { label: 'Items', value: `${ev.item_count ?? 0} · ${formatEventAmount(eventCustomerTotalAmount(ev))}` },
        ],
        actions: [
          { label: 'Open record', to: `/events/${ev.id}` },
          { label: 'Schedule', to: `/events/${ev.id}/schedule`, key: 's' },
          { label: 'Client view', to: `/events/${ev.id}/client-view`, key: 'v' },
        ],
      }];
    }));
    // The row helpers are plain functions of the row, redeclared each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ListPreviewPanel, sortedDisplayEvents]);
  const getEventPreview = React.useCallback((id: string) => eventPreviews?.get(id) ?? null, [eventPreviews]);
  const showSummaryQuery = useShowPeriodSummaries(sortedDisplayEvents.filter(ev =>
    groupBy === 'none' || !collapsedGroups.has(groupLabel(groupBy, ev))).map(ev => ev.id));
  function showPeriodBadge(eventId: string) {
    const summary = showSummaryQuery.data?.[eventId];
    return summary?.advanced ? <span className="show-period-status__badge show-period-status__badge--compact"
      title={`${summary.periods.filter(period => period.complete).length} of ${summary.periods.length} shows complete`}>
      {summary.betweenShows ? 'Between shows' : `${summary.periods.length} shows`}</span> : null;
  }
  // Enrich events with resolved SP display names for calendar tooltips/day view
  const calendarEvents = React.useMemo(() => {
    if (viewMode !== 'calendar') return displayEvents;
    return displayEvents.map((ev) => {
      const spName = formatUserSummary(salesManagerNameMap, ev.sales_manager_id);
      if (!spName || ev.sales_manager_display_name) return ev;
      return { ...ev, sales_manager_display_name: spName };
    });
  }, [displayEvents, salesManagerNameMap, viewMode]);
  const hasAdvancedFilters = stageFilterActive
    || Boolean(startDateFrom)
    || Boolean(startDateTo)
    || Boolean(endDateFrom)
    || Boolean(endDateTo)
    || pmFilter.length > 0
    || spFilter.length > 0
    || filterOverdue
    || filterUrgent
    || fulfillmentFilter !== 'all';
  const frequentFilterCount = Number(stageFilterActive)
    + Number(siteFilterChanged)
    + Number(groupBy !== 'none')
    + Number(filterOverdue)
    + Number(filterUrgent);
  const loadingInitial = loading && allEvents.length === 0;
  const matchingEventCount = totalCount > displayEvents.length ? totalCount : displayEvents.length;
  const showEventsLabel = loadingInitial
    ? 'Show events'
    : `Show ${matchingEventCount.toLocaleString()} ${matchingEventCount === 1 ? 'event' : 'events'}`;
  // Overdue and Urgent, in the toolbar's filter row or the phone filter sheet. In the sheet the
  // group's gap spaces them, so they drop the toolbar's extra margin.
  const renderFlagFilterButtons = (inSheet: boolean) => (
    <>
      {(
        <button
          type="button"
          className={`pill sm ${filterOverdue ? 'active' : ''}`}
          aria-pressed={filterOverdue}
          onClick={() => setFilterOverdue(f => !f)}
          title={`Filter to overdue events (${overdueCount.overdue ?? 'unavailable'})`}
          style={inSheet ? undefined : { marginLeft: '0.35rem' }}
        >
          <span className="event-flag event-flag--overdue" style={{ padding: 0, background: 'none', fontSize: 'inherit' }}>
            <svg className="event-flag__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: '.85em', height: '.85em' }}>
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 4v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="8" cy="11" r="0.75" fill="currentColor" />
            </svg>
          </span>
          {' '}Overdue ({overdueCount.overdue ?? '—'})
        </button>
      )}
      {(
        <button
          type="button"
          className={`pill sm ${filterUrgent ? 'active' : ''}`}
          aria-pressed={filterUrgent}
          onClick={() => setFilterUrgent(f => !f)}
          title={`Filter to urgent events (${overdueCount.urgent ?? 'unavailable'})`}
          style={inSheet ? undefined : { marginLeft: '0.25rem' }}
        >
          <span className="event-flag event-flag--urgent" style={{ padding: 0, background: 'none', fontSize: 'inherit' }}>
            <svg className="event-flag__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: '.85em', height: '.85em' }}>
              <path d="M8 1.5L14.5 13.5H1.5L8 1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              <path d="M8 6v3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <circle cx="8" cy="11.5" r="0.65" fill="currentColor" />
            </svg>
          </span>
          {' '}Urgent ({overdueCount.urgent ?? '—'})
        </button>
      )}
    </>
  );
  const calendarRowsLoading = isCalendar && (!calendarRange || loadingInitial);
  const eventHeaderStats = React.useMemo(() => {
    return summarizeEventHeaderRows(
      displayEvents,
      confirmedPipelineStatuses,
      (event) => event.status,
      eventCustomerTotalAmount,
    );
  }, [confirmedPipelineStatuses, displayEvents]);
  const eventStatsScope = viewMode === 'calendar'
    ? 'Current calendar window'
    : (totalCount > displayEvents.length ? 'Visible page' : 'Current filters');
  const confirmedTotalQuery = isCalendar ? calendarConfirmedTotalQuery : filteredConfirmedTotalQuery;
  const confirmedSummary = confirmedTotalQuery.data;
  const confirmedHeaderStatsLoading = !eventsQuery.isSuccess || confirmedTotalQuery.isPending;
  const confirmedCountHint = isCalendar
    ? 'Selected sites; confirmed-invoiced'
    : 'All matching events; excludes lost/cancelled';
  const confirmedTotalHint = isCalendar
    ? 'Incl. tax; selected sites; confirmed-invoiced'
    : 'Incl. tax; all matching events; excludes lost/cancelled';
  const eventHeaderStatValues = {
    visibleCount: loadingInitial ? '...' : eventHeaderStats.visibleCount.toLocaleString(),
    visibleGrandTotal: loadingInitial ? '...' : formatEventAmount(eventHeaderStats.visibleGrandTotal),
    confirmedCount: confirmedTotalQuery.isError ? '—' : confirmedHeaderStatsLoading ? '...' : (confirmedSummary?.count ?? 0).toLocaleString(),
    confirmedGrandTotal: confirmedTotalQuery.isError ? '—' : confirmedHeaderStatsLoading ? '...' : formatEventAmount(confirmedSummary?.total ?? 0),
  };

  // A phone's card holds every column the list row shows: stage (with flags, shows and
  // availability), R2 #, name, customer, venue, dates, total, quantity, PM, SP and the actions.
  const renderEventCard = (ev: EventRow) => {
    const href = `/events/${ev.id}`;
    const startDisplay = formatDateOnly(eventStartDateValue(ev));
    const endDisplay = formatDateOnly(eventEndDateValue(ev));
    const dates = startDisplay && endDisplay
      ? (startDisplay === endDisplay ? startDisplay : `${startDisplay} → ${endDisplay}`)
      : (startDisplay || endDisplay || 'Dates not set');
    const statusSummary = orderStatusSummary(ev);
    const stageInfo = deriveOrderStage(extractEventOrderStatuses(ev), orderStageLookup);
    const nextIndex = orderedStatuses.findIndex(s => s.status === (stageInfo?.status ?? ''));
    const nextStatus = nextIndex >= 0 && nextIndex < orderedStatuses.length - 1 ? orderedStatuses[nextIndex + 1]?.status ?? null : null;
    const avail = availabilityMap.get(ev.id);
    const availStatus = availabilityStatusFromRatio(avail?.ratio, avail?.returnWarning);
    const availTip = availabilityTooltip(avail?.ratio, avail?.returnWarning, avail?.timeError);
    const showAvailability = availabilityMode !== 'off' && avail !== undefined && availStatus !== 'unknown';
    const barText = shortageBarText(avail);
    const customer = ev.client_display_name ?? ev.client_business ?? '';
    const venue = primaryVenueName(ev);
    const items = ev.item_count ?? 0;
    const pm = ev.project_manager_display_name;
    const sp = salesManagerDisplayName(ev);
    return (
      <LibraryRecordCard
        key={ev.id}
        href={href}
        onOpen={() => navigate(href)}
        code={ev.event_code || ev.r2_contract_iid || '—'}
        badges={(
          <>
            <span className="status-chip library-record-card__pill" data-tone="neutral" title={statusSummary}>{statusSummary}</span>
            <EventFlags row={overdueMap.get(ev.id) ?? null} />
            {showPeriodBadge(ev.id)}
            {/* Bar shows its shortage words; Glow and Icon show the dot (a card has no row to glow). */}
            {showAvailability ? (
              availabilityMode === 'bar' && barText
                ? <span className={`event-row__bar-label event-row__bar-label--${availStatus}`} title={availTip}>{barText}</span>
                : <span className={`avail-icon avail-icon--${availStatus}`} title={availTip} role="img" aria-label={availTip} />
            ) : null}
          </>
        )}
        title={eventDisplayName(ev.name, ev.customer_pickup) || 'Untitled Event'}
        details={customer || venue ? (
          <>
            {customer ? <LibraryRecordCardDetail>{customer}</LibraryRecordCardDetail> : null}
            {venue ? <LibraryRecordCardDetail icon={<LocationPinIcon />} muted>{venue}</LibraryRecordCardDetail> : null}
          </>
        ) : undefined}
        meta={[
          dates,
          <span key="total" className="library-record-card__emphasis">{formatEventAmount(eventCustomerTotalAmount(ev))}</span>,
          items > 0 ? `${items.toLocaleString()} item${items === 1 ? '' : 's'}` : null,
          `PM ${pm ?? '—'}`,
          `SP ${sp ?? '—'}`,
        ]}
        actions={(isManager || isAdmin) ? (
          <EventActionsDropdown
            event={ev}
            currentOrderStatus={stageInfo?.status ?? null}
            nextOrderStatus={nextStatus}
            orderedStatuses={orderedStatuses}
            onDelete={handleDeleteEvent}
            onStageUpdated={handleStageUpdated}
          />
        ) : undefined}
      />
    );
  };

  const renderGroupedSections = () => {
    const groups = new Map<string, EventRow[]>();
    for(const ev of displayEvents){
      const key = groupLabel(groupBy, ev);
      groups.set(key, [ ...(groups.get(key)||[]), ev ]);
    }
    const sections = Array.from(groups.entries());
    if (groupBy === 'date') {
      sections.sort((a, b) => {
        const ad = Date.parse(`01 ${a[0]}`);
        const bd = Date.parse(`01 ${b[0]}`);
        if (!Number.isNaN(ad) && !Number.isNaN(bd)) return (ad - bd) * (listSortDir === 'asc' ? 1 : -1);
        return a[0].localeCompare(b[0]);
      });
    } else {
      sections.sort((a, b) => a[0].localeCompare(b[0]));
    }

    return (
      <div className={isPhone ? 'stack gap-md library-record-card-groups' : 'stack gap-md'}>
        {sections.map(([label, rows]) => {
          const isCollapsed = collapsedGroups.has(label);
          return (
          <div key={label} className="stack">
            <button
              type="button"
              className="events-group-header"
              onClick={() => toggleGroupCollapsed(label)}
              aria-expanded={!isCollapsed}
              title={isCollapsed ? 'Expand group' : 'Collapse group'}
              style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            >
              <span aria-hidden>{isCollapsed ? '▶ ' : '▼ '}</span>
              {label}
              <span className="fg-muted" style={{ fontWeight: 400, marginLeft: '0.35rem' }}>({rows.length})</span>
            </button>
            {!isCollapsed && isPhone ? (
              <LibraryRecordCardList label={`Events: ${label}`}>{sortRows(rows).map(renderEventCard)}</LibraryRecordCardList>
            ) : null}
            {!isCollapsed && !isPhone && (
            <table className="data-table" aria-label={`Events: ${label}`}>
              <LibraryTableHead>
                <tr>
                  <th className="el-col-stage" role="columnheader" aria-sort={ariaSort('stage')} onDoubleClick={()=>handleHeaderSort('stage')} style={{cursor:'pointer'}} title="Double-click to sort">Stage {sortIndicator(listSortKey==='stage')}</th>
                  <th className="el-col-r2" role="columnheader" aria-sort={ariaSort('r2')} onDoubleClick={()=>handleHeaderSort('r2')} style={{cursor:'pointer'}} title="Double-click to sort">R2 # {sortIndicator(listSortKey==='r2')}</th>
                  <th className="el-col-name" role="columnheader" aria-sort={ariaSort('name')} onDoubleClick={()=>handleHeaderSort('name')} style={{cursor:'pointer'}} title="Double-click to sort">Name {sortIndicator(listSortKey==='name')}</th>
                  <th className="el-col-start" role="columnheader" aria-sort={ariaSort('date')} onDoubleClick={()=>handleHeaderSort('date')} style={{cursor:'pointer'}} title="Double-click to sort">Start {sortIndicator(listSortKey==='date')}</th>
                  <th className="el-col-end" role="columnheader" aria-sort={ariaSort('endDate')} onDoubleClick={()=>handleHeaderSort('endDate')} style={{cursor:'pointer'}} title="Double-click to sort">End {sortIndicator(listSortKey==='endDate')}</th>
                  <th className="el-col-venue" role="columnheader" aria-sort={ariaSort('venue')} onDoubleClick={()=>handleHeaderSort('venue')} style={{cursor:'pointer'}} title="Double-click to sort">Venue {sortIndicator(listSortKey==='venue')}</th>
                  <th className="el-col-net" role="columnheader" aria-sort={ariaSort('netAmount')} onDoubleClick={()=>handleHeaderSort('netAmount')} style={{cursor:'pointer'}} title="Double-click to sort by customer total">Total {sortIndicator(listSortKey==='netAmount')}</th>
                  <th className="el-col-items" role="columnheader" aria-sort={ariaSort('items')} onDoubleClick={()=>handleHeaderSort('items')} style={{cursor:'pointer'}} title="Double-click to sort">Qty {sortIndicator(listSortKey==='items')}</th>
                  <th className="el-col-customer" role="columnheader" aria-sort={ariaSort('customer')} onDoubleClick={()=>handleHeaderSort('customer')} style={{cursor:'pointer'}} title="Double-click to sort">Customer {sortIndicator(listSortKey==='customer')}</th>
                  <th className="el-col-pm" role="columnheader" aria-sort={ariaSort('pm')} onDoubleClick={()=>handleHeaderSort('pm')} style={{cursor:'pointer'}} title="Double-click to sort">PM {sortIndicator(listSortKey==='pm')}</th>
                  <th className="el-col-sp" role="columnheader" aria-sort={ariaSort('sp')} onDoubleClick={()=>handleHeaderSort('sp')} style={{cursor:'pointer'}} title="Double-click to sort">SP {sortIndicator(listSortKey==='sp')}</th>
                  {availabilityMode === 'icon' && <th className="el-col-avail-icon" style={{ width: '18px' }} aria-label="Availability" />}
                  {(isManager || isAdmin) && <th className="el-col-actions">Actions</th>}
                </tr>
              </LibraryTableHead>
              <tbody>
                {sortRows(rows).map((ev) => {
                  const startDisplay = formatDateOnly(eventStartDateValue(ev));
                  const endDisplay = formatDateOnly(eventEndDateValue(ev));
                  const statusSummary = orderStatusSummary(ev);
                  const avail = availabilityMap.get(ev.id);
                  const ratio = avail?.ratio;
                  const availStatus = availabilityStatusFromRatio(ratio, avail?.returnWarning);
                  const availTip = availabilityTooltip(ratio, avail?.returnWarning, avail?.timeError);
                  const statuses = extractEventOrderStatuses(ev);
                  const groupStageInfo = deriveOrderStage(statuses, orderStageLookup);
                  const groupNextStatus = (() => {
                    const idx = orderedStatuses.findIndex(s => s.status === (groupStageInfo?.status ?? ''));
                    return idx >= 0 && idx < orderedStatuses.length - 1 ? orderedStatuses[idx + 1]?.status ?? null : null;
                  })();
                  const hasAvailability = avail !== undefined;
                  const rowGlowClass = hasAvailability && availabilityMode === 'glow' ? `event-row--avail-${availStatus}` : '';
                  const rowBarClass = hasAvailability && availabilityMode === 'bar' && availStatus !== 'unknown' ? `event-row--bar event-row--bar-${availStatus}` : '';
                  const barText = shortageBarText(avail);
                  return (
                    <tr key={ev.id} className={`${rowGlowClass} ${rowBarClass}`.trim()} title={hasAvailability ? availTip : undefined}>
                      <td data-label="Stage" className="truncate" title={statusSummary}>
                        {statusSummary} <EventFlags row={overdueMap.get(ev.id) ?? null} compact />
                        {showPeriodBadge(ev.id)}
                        {availabilityMode === 'bar' && barText && <span className={`event-row__bar-label event-row__bar-label--${availStatus}`}>{barText}</span>}
                      </td>
                      <td data-label="R2 #"><Link to={`/events/${ev.id}`}>{ev.event_code || ev.r2_contract_iid || ''}</Link></td>
                      <td data-label="Name" className="truncate" title={eventDisplayName(ev.name, ev.customer_pickup)}><Link to={`/events/${ev.id}`}>{eventDisplayName(ev.name, ev.customer_pickup) || 'Untitled Event'}</Link></td>
                      <td data-label="Start">{startDisplay}</td>
                      <td data-label="End">{endDisplay}</td>
                      <td data-label="Venue" className="truncate" title={primaryVenueName(ev)||''}>{primaryVenueName(ev) || ''}</td>
                      <td data-label="Total">{formatEventAmount(eventCustomerTotalAmount(ev))}</td>
                      <td data-label="Qty">{(ev.item_count ?? 0) > 0 ? ev.item_count : ''}</td>
                      <td data-label="Customer" className="truncate" title={ev.client_display_name ?? ev.client_business ?? ''}>{ev.client_display_name ?? ev.client_business ?? ''}</td>
                      <td data-label="PM" className="truncate" title={ev.project_manager_display_name ?? ''}>{ev.project_manager_display_name ?? '—'}</td>
                      <td data-label="SP" className="truncate" title={salesManagerDisplayName(ev) ?? ''}>{salesManagerDisplayName(ev) ?? '—'}</td>
                      {availabilityMode === 'icon' && (
                        <td data-label="Availability" className="el-col-avail-icon" style={{ width: '18px', textAlign: 'center', padding: '0 2px' }}>
                          {hasAvailability && availStatus !== 'unknown' && <span className={`avail-icon avail-icon--${availStatus}`} title={availTip} />}
                        </td>
                      )}
                      {(isManager || isAdmin) && (
                        <td data-label="Actions">
                          <EventActionsDropdown
                            event={ev}
                            currentOrderStatus={groupStageInfo?.status ?? null}
                            nextOrderStatus={groupNextStatus}
                            orderedStatuses={orderedStatuses}
                            onDelete={handleDeleteEvent}
                            onStageUpdated={handleStageUpdated}
                            stopPropagation={false}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
          );
        })}
      </div>
    );
  };
  return (
    <div className="settings-shell settings-page library-page library-page--phone events-page">
      {showSummaryQuery.isError ? <div role="alert" className="alert alert-error">Show-period status could not be refreshed.
        <button type="button" className="btn xs subtle" onClick={() => void showSummaryQuery.refetch()}>Retry show status</button>
      </div> : null}
      {confirmedTotalQuery.isError ? (
        <div role="alert" className="alert alert-error">
          Confirmed event totals could not be loaded.
          <button type="button" className="btn xs subtle" onClick={() => void confirmedTotalQuery.refetch()}>Retry confirmed totals</button>
        </div>
      ) : null}
      <div className="settings-page__pinned">
        <header className="settings-hero library-phone-hero">
          <div className="settings-hero__headline library-phone-hero__group">
            <span className="settings-hero__eyebrow">Library</span>
            <div className="settings-hero__title-row">
              <h1 className="settings-card__title m0">Events</h1>
            </div>
            <p className="settings-hero__body">
              {isPhone ? 'R2 orders and site surveys.' : 'R2 orders and site survey projects live together in one library view.'}
            </p>
          </div>
          <div ref={eventsActionsGuideRef} className="settings-hero__actions events-hero-actions">
            <button className="btn btn-primary" onClick={() => setShowWizard(true)}>
              <span style={{fontSize:'1.1rem', lineHeight:1}}>+</span> Create Event
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowLeaderboard(true)} title="Event Leaderboard" aria-label="Event Leaderboard">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
              Leaderboard
            </button>
            {beoImportEnabled && (
              <>
                <button className="btn" onClick={() => setShowBeoUploadModal(true)}>
                  Import BEO
                </button>
                {import.meta.env.DEV && (
                  <button className="btn btn-ghost btn-sm" onClick={handleBeoTestFixture} title="Load test fixture (dev only)">
                    Test BEO
                  </button>
                )}
              </>
            )}
            <GuideQuestionButton
              className="guide-question-button--inline"
              onClick={eventsGuide.start}
              disabled={!eventsGuide.canLaunch}
              title="Open Events guide"
              ariaLabel="Open Events guide"
            />
          </div>
          <div className="settings-hero__meta events-hero-stats" aria-label="Current event result stats" data-stats-toggle-label="Stats">
            <div className="events-hero-stat">
              <span className="events-hero-stat__label">Events Shown</span>
              <span className="events-hero-stat__value">{eventHeaderStatValues.visibleCount}</span>
              <span className="events-hero-stat__hint">{eventStatsScope}</span>
            </div>
            <div className="events-hero-stat">
              <span className="events-hero-stat__label">Grand Total Shown</span>
              <span className="events-hero-stat__value">{eventHeaderStatValues.visibleGrandTotal}</span>
              <span className="events-hero-stat__hint">{eventStatsScope}; incl. tax</span>
            </div>
            <div className="events-hero-stat">
              <span className="events-hero-stat__label">Events Confirmed+</span>
              <span className="events-hero-stat__value">{eventHeaderStatValues.confirmedCount}</span>
              <span className="events-hero-stat__hint">{confirmedCountHint}</span>
            </div>
            <div className="events-hero-stat">
              <span className="events-hero-stat__label">Grand Total Confirmed+</span>
              <span className="events-hero-stat__value">{eventHeaderStatValues.confirmedGrandTotal}</span>
              <span className="events-hero-stat__hint">{confirmedTotalHint}</span>
            </div>
          </div>
        </header>

        <div ref={eventsToolbarGuideRef} className="library-toolbar-block" data-open={advancedOpen} data-active={hasAdvancedFilters}>
          <div className="card soft library-toolbar">
            <div className="library-toolbar__search library-toolbar__search--wide">
              <LibrarySearchInput
                value={search}
                onChange={setSearch}
                placeholder={isPhone ? 'Search name, R2, customer' : 'Search by name, R2, customer, venue, PM, SP…'}
                label="Search events"
              />
              <LibraryFilterToggle
                ref={filterToggleRef}
                expanded={filterSheetOpen}
                activeCount={frequentFilterCount}
                aria-haspopup="dialog"
                onClick={() => {
                  setAdvancedOpen(false);
                  setFilterSheetOpen(true);
                }}
              />
            </div>
            {compactToolbar ? null : (
            <div className="library-toolbar__mobile-panel">
              <div className="library-toolbar__filters">
                <label className="library-toolbar__field text-xxs fg-muted">
                  <span>Group</span>
                  <select className="xs" value={groupBy} onChange={handleGroupByChange}>
                    {EVENT_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label className="library-toolbar__field text-xxs fg-muted" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span>Sort</span>
                  <select
                    className="xs"
                    value={listSortKey}
                    onChange={handleListSortKeyChange}
                    aria-label="Sort by column (applies to all matching results)"
                  >
                    {EVENT_SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button
                    type="button"
                    className="btn micro"
                    onClick={toggleListSortDir}
                    title={listSortDir === 'asc' ? 'Ascending (click for descending)' : 'Descending (click for ascending)'}
                    aria-label={listSortDir === 'asc' ? 'Sort ascending; click to switch to descending' : 'Sort descending; click to switch to ascending'}
                    style={{ minWidth: '28px', padding: '0.25rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {listSortDir === 'asc' ? (
                      <span aria-hidden="true">▲</span>
                    ) : (
                      <span aria-hidden="true">▼</span>
                    )}
                  </button>
                </label>
                <div className="library-toolbar__field library-toolbar__field--site text-xxs fg-muted">
                  <span>Site</span>
                  <SearchableMultiSelect
                    options={siteFilterOptions}
                    selected={siteFilters}
                    onChange={handleSiteFiltersChange}
                    placeholder="All sites"
                    ariaLabel="Filter by site"
                    className="events-site-filter"
                    selectionSummary={siteFilterSummary}
                    showPills={false}
                  />
                </div>
                {siteOptionsLoading && <span className="text-xxs fg-muted">Loading sites...</span>}
                {siteOptionsError && !siteOptionsLoading && <span className="text-xxs fg-error">{siteOptionsError}</span>}
              </div>
              <div className="library-toolbar__stages">
                <StageFilterToolbar
                  selectedStatuses={selectedStageStatuses}
                  onSelectedStatusesChange={setStageFilterStatuses}
                  stageGroups={stageGroups}
                  standaloneStatuses={standaloneStatuses}
                  orderStageLookup={orderStageLookup}
                  fallbackStatuses={fallbackStageStatuses}
                  defaultSelectedStatuses={defaultStageStatuses}
                  compact
                />
                {renderFlagFilterButtons(false)}
              </div>
            </div>
            )}
            <div className="library-toolbar__meta">
              {/* Phones say how many match; the pager under the cards says which page this is. */}
              <div className="lib-count">
                {isPhone && viewMode !== 'calendar' ? (
                  <>{Math.max(totalCount, displayEvents.length).toLocaleString()} {Math.max(totalCount, displayEvents.length) === 1 ? 'event' : 'events'}</>
                ) : (
                  <>
                    Showing {displayEvents.length.toLocaleString()}
                    {totalCount > displayEvents.length ? <> of {totalCount.toLocaleString()}</> : null} events
                  </>
                )}
                {(filterOverdue || filterUrgent) ? <span className="fg-muted"> (filtered)</span> : null}
              </div>
              <LibraryViewToggle
                value={isPhone && viewMode === 'grid' ? 'list' : viewMode}
                ariaLabel="Event library view"
                options={isPhone ? PHONE_VIEW_OPTIONS : VIEW_OPTIONS}
                onChange={setViewMode}
              />
            </div>
            <LibraryAdvancedToggle
              expanded={advancedOpen}
              active={hasAdvancedFilters}
              onClick={() => setAdvancedOpen((open) => !open)}
            />
          </div>
          <div className="library-toolbar__advanced" data-open={advancedOpen}>
            <div className="library-toolbar__advanced-grid">
              <div className="library-toolbar__field text-xxs fg-muted" role="group" aria-label="Start date range">
                <span>Start date range</span>
                <div className="library-toolbar__range">
                  <DateFilterInput label="Start date from" value={startDateFrom} onChange={setStartDateFrom} />
                  <span aria-hidden="true">to</span>
                  <DateFilterInput label="Start date to" value={startDateTo} onChange={setStartDateTo} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted" role="group" aria-label="End date range">
                <span>End date range</span>
                <div className="library-toolbar__range">
                  <DateFilterInput label="End date from" value={endDateFrom} onChange={setEndDateFrom} />
                  <span aria-hidden="true">to</span>
                  <DateFilterInput label="End date to" value={endDateTo} onChange={setEndDateTo} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Project manager</span>
                <SearchableMultiSelect
                  options={pmOptions}
                  selected={pmFilter}
                  onChange={setPmFilter}
                  placeholder="All PMs"
                  ariaLabel="Filter by project manager"
                />
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Sales person</span>
                <SearchableMultiSelect
                  options={spOptions}
                  selected={spFilter}
                  onChange={setSpFilter}
                  placeholder="All SPs"
                  ariaLabel="Filter by sales person"
                />
              </div>
            </div>
              <div className="library-toolbar__advanced-section">
              <span className="library-toolbar__advanced-section-label text-xxs fg-muted">Fulfillment</span>
              <div className="avail-mode-picker" role="radiogroup" aria-label="Fulfillment method filter">
                {([['all', 'All'], ['customer_pickup', 'Customer Pickup'], ['ita_delivery', 'ITA Delivery']] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={fulfillmentFilter === value}
                    className={`avail-mode-picker__btn text-xxs${fulfillmentFilter === value ? ' avail-mode-picker__btn--active' : ''}`}
                    onClick={() => setFulfillmentFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="library-toolbar__advanced-section">
              <span className="library-toolbar__advanced-section-label text-xxs fg-muted">Availability</span>
              <div className="avail-mode-picker" role="radiogroup" aria-label="Availability display mode">
                {([['off', 'Off'], ['glow', 'Glow'], ['bar', 'Bar'], ['icon', 'Icon']] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={availabilityMode === value}
                    className={`avail-mode-picker__btn text-xxs${availabilityMode === value ? ' avail-mode-picker__btn--active' : ''}`}
                    onClick={() => setAvailabilityMode(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <LibrarySavedViews controller={savedViewsController} />
            <div className="library-toolbar__advanced-actions">
              <button type="button" className="btn micro" onClick={resetAdvancedFilters} disabled={!hasAdvancedFilters}>Reset filters</button>
            </div>
          </div>
        </div>
        <LibraryFilterSheet
          open={filterSheetOpen && compactToolbar}
          onClose={() => setFilterSheetOpen(false)}
          onReset={resetFrequentFilters}
          resetDisabled={frequentFilterCount === 0}
          resultLabel={showEventsLabel}
          returnFocusRef={filterToggleRef}
        >
          <LibraryFilterSheetField label="Group">
            <select value={groupBy} onChange={handleGroupByChange}>
              {EVENT_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </LibraryFilterSheetField>
          <LibraryFilterSheetGroup label="Sort">
            <select value={listSortKey} onChange={handleListSortKeyChange} aria-label="Sort by">
              {EVENT_SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <button
              type="button"
              className="btn btn-outline"
              onClick={toggleListSortDir}
              aria-label={listSortDir === 'asc' ? 'Sorted ascending; switch to descending' : 'Sorted descending; switch to ascending'}
            >
              {listSortDir === 'asc' ? 'Ascending' : 'Descending'}
            </button>
          </LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="Site">
            <SearchableMultiSelect
              options={siteFilterOptions}
              selected={siteFilters}
              onChange={handleSiteFiltersChange}
              placeholder="All sites"
              ariaLabel="Filter by site"
              className="events-site-filter"
              selectionSummary={siteFilterSummary}
              showPills={false}
            />
            {siteOptionsLoading ? <span className="fg-muted">Loading sites...</span> : null}
            {siteOptionsError && !siteOptionsLoading ? <span className="fg-error">{siteOptionsError}</span> : null}
          </LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="Stage">
            <StageFilterToolbar
              selectedStatuses={selectedStageStatuses}
              onSelectedStatusesChange={setStageFilterStatuses}
              stageGroups={stageGroups}
              standaloneStatuses={standaloneStatuses}
              orderStageLookup={orderStageLookup}
              fallbackStatuses={fallbackStageStatuses}
              defaultSelectedStatuses={defaultStageStatuses}
            />
          </LibraryFilterSheetGroup>
          <LibraryFilterSheetGroup label="Flags">{renderFlagFilterButtons(true)}</LibraryFilterSheetGroup>
          {/* Phones page with Previous and Next under the cards; the page size lives here. */}
          {isPhone && viewMode !== 'calendar' ? (
            <LibraryFilterSheetField label="Per page">
              <select value={pageSize} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </LibraryFilterSheetField>
          ) : null}
        </LibraryFilterSheet>
      </div>

      <div ref={eventsResultsGuideRef} className="settings-content">
        {/* Initial skeletons: show only when first loading and no data yet (never in calendar view to avoid blank cards flashing when changing months) */}
        {loading && events.length === 0 && viewMode !== 'calendar' && (
          isPhone ? (
            <LibraryRecordCardList label="Loading events" busy>
              {[0, 1, 2].map((index) => <LibraryRecordCardSkeleton key={index} />)}
            </LibraryRecordCardList>
          ) : viewMode === 'list' ? (
            <table className="skeleton-table"><tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}><td style={{width:'12%'}}><div className="skel-bar" /></td><td style={{width:'25%'}}><div className="skel-bar" /></td><td style={{width:'18%'}}><div className="skel-bar" /></td><td style={{width:'15%'}}><div className="skel-bar" /></td><td><div className="skel-bar" /></td></tr>
              ))}
            </tbody></table>
          ) : (
            <div className="events-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="event-card skeleton">
                  <div style={{height:18,width:90,borderRadius:6,marginBottom:16}} />
                  <div style={{height:20,width:'70%',borderRadius:8,marginBottom:12}} />
                  <div style={{height:14,width:'45%',borderRadius:6}} />
                </div>
              ))}
            </div>
          )
        )}

        {error && (
          <ErrorBanner
            message={error}
            onRetry={() => eventsQuery.refetch()}
            reportContext={{ page: '/events', search: debouncedSearch, viewMode, groupBy: groupBy }}
          />
        )}

        {availabilityMode !== 'off' && availabilityEvents.map(event => {
          const timeError = availabilityMap.get(event.id)?.timeError;
          return timeError ? <ErrorBanner key={event.id} message={timeError} action={(
            <Link className="btn xs subtle" to={`/events/${event.id}/schedule`}>
              Review time for {event.name || event.event_code || 'event'}
            </Link>
          )} /> : null;
        })}

        {calendarRowsLoading && (
          <LoadingState message="Loading event calendar..." />
        )}

        {/* Subtle refresh indicator keeps list in place while fetching */}
        {refreshing && events.length > 0 && (
          <LoadingState message="Refreshing events..." className="refresh-indicator" />
        )}

        {viewMode === 'calendar' ? (
          <EventsCalendarView events={calendarEvents} showPeriodSummaries={showSummaryQuery.data} overdueMap={overdueMap} onDateRangeChange={handleCalendarDateRangeChange} availabilityMap={availabilityMap} availabilityMode={availabilityMode} isActionable={isManager || isAdmin} onDelete={handleDeleteEvent} onStageUpdated={handleStageUpdated} stageLegendItems={orderedStatuses} showWarehouseWhiskers={showCalendarWarehouseWhiskers} onShowWarehouseWhiskersChange={setShowCalendarWarehouseWhiskers} showHiddenEvents={canRevealHiddenCalendarEvents && showHiddenCalendarEvents} onShowHiddenEventsChange={setShowHiddenCalendarEvents} hiddenEventCount={hiddenCalendarEventCount} canRevealHiddenEvents={canRevealHiddenCalendarEvents} />
        ) : events.length === 0 && !loading && !error ? (
          <div className="empty-state">
            <div className="empty-icon">*</div>
            <h3>No events match your filters</h3>
            <p>Try adjusting the site or advanced filters above.</p>
            <button className="btn btn-primary" onClick={() => setShowWizard(true)}>Create Event</button>
          </div>
        ) : error && events.length === 0 ? null : isPhone ? (
          sortedDisplayEvents.length === 0 ? null
            : groupBy === 'none'
              ? <LibraryRecordCardList label="Events">{sortedDisplayEvents.map(renderEventCard)}</LibraryRecordCardList>
              : renderGroupedSections()
        ) : (
          viewMode === 'grid' ? (
            (() => {
              const gridSections: [string, EventRow[]][] =
                groupBy === 'none'
                  ? [['', displayEvents]]
                  : (() => {
                      const groups = new Map<string, EventRow[]>();
                      for (const ev of events) {
                        const key = groupLabel(groupBy, ev);
                        groups.set(key, [...(groups.get(key) || []), ev]);
                      }
                      const sections = Array.from(groups.entries());
                      if (groupBy === 'date') {
                        sections.sort((a, b) => {
                          const ad = Date.parse(`01 ${a[0]}`);
                          const bd = Date.parse(`01 ${b[0]}`);
                          if (!Number.isNaN(ad) && !Number.isNaN(bd)) return (ad - bd) * (listSortDir === 'asc' ? 1 : -1);
                          return a[0].localeCompare(b[0]);
                        });
                      } else {
                        sections.sort((a, b) => a[0].localeCompare(b[0]));
                      }
                      return sections;
                    })();
              const renderCard = (ev: EventRow) => {
                const startDisplay = formatDateOnly(eventStartDateValue(ev));
                const endDisplay = formatDateOnly(eventEndDateValue(ev));
                const statuses = extractEventOrderStatuses(ev);
                const stageInfo = deriveOrderStage(statuses, orderStageLookup);
                const avail = availabilityMap.get(ev.id);
                const ratio = avail?.ratio;
                const availStatus = availabilityStatusFromRatio(ratio, avail?.returnWarning);
                const availTip = availabilityTooltip(ratio, avail?.returnWarning, avail?.timeError);
                const hasAvailability = avail !== undefined;
                const cardGlowClass = hasAvailability && availabilityMode === 'glow' ? `event-card--avail-${availStatus}` : '';
                const barText = shortageBarText(avail);
                return (
                  <div key={ev.id} className={`event-card modern${(isManager || isAdmin) ? ' event-card--has-actions' : ''} ${cardGlowClass}`.trim()} style={{ position: 'relative' }}>
                    <div className="event-card__top-bar">
                      <span className="event-card__stage pill subtle" title={stageInfo ? `Order stage: ${stageInfo.label}` : (ev.status ? `Order stage: ${formatOrderStatusLabel(ev.status)}` : 'Order stage')}>
                        {stageInfo ? stageInfo.label : (ev.status ? formatOrderStatusLabel(ev.status) : '—')}
                      </span>
                      {availabilityMode === 'icon' && hasAvailability && availStatus !== 'unknown' && (
                        <span className={`avail-icon avail-icon--${availStatus}`} title={availTip} />
                      )}
                      {(isManager || isAdmin) && <span className="event-card__actions-spacer" aria-hidden="true" />}
                    </div>
                    {(isManager || isAdmin) && (
                      <div className="event-card__actions" onClick={(e) => e.stopPropagation()}>
                        <EventActionsDropdown
                          event={ev}
                          currentOrderStatus={stageInfo?.status ?? null}
                          nextOrderStatus={
                            (() => {
                              const idx = orderedStatuses.findIndex(s => s.status === (stageInfo?.status ?? ''));
                              return idx >= 0 && idx < orderedStatuses.length - 1 ? orderedStatuses[idx + 1]?.status ?? null : null;
                            })()
                          }
                          orderedStatuses={orderedStatuses}
                          onDelete={handleDeleteEvent}
                          onStageUpdated={handleStageUpdated}
                        />
                      </div>
                    )}
                    <Link to={`/events/${ev.id}`} className="event-card__link" title={hasAvailability ? availTip : undefined} aria-label={hasAvailability ? availTip : undefined}>
                      <div className="event-card__title-line event-card__title-line--single">
                        <EventFlags row={overdueMap.get(ev.id) ?? null} compact />
                        <span className="event-card__r2">{ev.event_code ?? '—'}</span>
                        <span className="event-card__title-sep"> – </span>
                        <span className="event-card__name">{eventDisplayName(ev.name, ev.customer_pickup) || 'Untitled Event'}</span>
                      </div>
                      <div className="event-card__venue text-xs fg-muted" title="Venue">{primaryVenueName(ev) || 'No Venue Linked'}</div>
                      {showPeriodBadge(ev.id)}
                      <div className="event-card__dates text-xs fg-muted" title="Dates">
                        {startDisplay && endDisplay ? `${startDisplay} → ${endDisplay}` : (startDisplay || endDisplay || 'Dates Not Set')}
                      </div>
                      <div className="event-card__pm-sp text-xs fg-muted" title="Project manager and sales person">
                        PM: {ev.project_manager_display_name ?? '—'} | SP: {salesManagerDisplayName(ev) ?? '—'}
                      </div>
                      <div className="event-card__footer text-xs fg-muted">
                        {formatEventAmount(eventCustomerTotalAmount(ev))}
                        {(ev.item_count ?? 0) > 0 ? ` · ${ev.item_count} item${(ev.item_count ?? 0) !== 1 ? 's' : ''}` : ''}
                      </div>
                    </Link>
                    {availabilityMode === 'bar' && hasAvailability && availStatus !== 'unknown' && barText && (
                      <div className={`event-card__avail-bar event-card__avail-bar--${availStatus}`}>{barText}</div>
                    )}
                  </div>
                );
              };
              return (
                <div className={groupBy === 'none' ? '' : 'stack gap-md'}>
                  {gridSections.map(([label, rows]) => {
                    const gridCollapsed = groupBy !== 'none' && collapsedGroups.has(label);
                    return (
                    <div key={label || '_all'} className="stack">
                      {groupBy !== 'none' && (
                        <button
                          type="button"
                          className="events-group-header"
                          onClick={() => toggleGroupCollapsed(label)}
                          aria-expanded={!gridCollapsed}
                          title={gridCollapsed ? 'Expand group' : 'Collapse group'}
                          style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                        >
                          <span aria-hidden>{gridCollapsed ? '▶ ' : '▼ '}</span>
                          {label}
                          <span className="fg-muted" style={{ fontWeight: 400, marginLeft: '0.35rem' }}>({rows.length})</span>
                        </button>
                      )}
                      {!gridCollapsed && (
                      <div className="events-grid">
                        {sortRows(rows).map(renderCard)}
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            // List view with optional grouping
            <div className="events-list-view" ref={listPreviewRef}>
              {groupBy === 'none' ? (
                <table className="data-table" aria-label="Events list">
                  <LibraryTableHead>
                    <tr>
                      <th className="el-col-stage" role="columnheader" aria-sort={ariaSort('stage')} onDoubleClick={()=>handleHeaderSort('stage')} style={{cursor:'pointer'}} title="Double-click to sort">Stage {sortIndicator(listSortKey==='stage')}</th>
                      <th className="el-col-r2" role="columnheader" aria-sort={ariaSort('r2')} onDoubleClick={()=>handleHeaderSort('r2')} style={{cursor:'pointer'}} title="Double-click to sort">R2 # {sortIndicator(listSortKey==='r2')}</th>
                      <th className="el-col-name" role="columnheader" aria-sort={ariaSort('name')} onDoubleClick={()=>handleHeaderSort('name')} style={{cursor:'pointer'}} title="Double-click to sort">Name {sortIndicator(listSortKey==='name')}</th>
                      <th className="el-col-start" role="columnheader" aria-sort={ariaSort('date')} onDoubleClick={()=>handleHeaderSort('date')} style={{cursor:'pointer'}} title="Double-click to sort">Start {sortIndicator(listSortKey==='date')}</th>
                      <th className="el-col-end" role="columnheader" aria-sort={ariaSort('endDate')} onDoubleClick={()=>handleHeaderSort('endDate')} style={{cursor:'pointer'}} title="Double-click to sort">End {sortIndicator(listSortKey==='endDate')}</th>
                      <th className="el-col-venue" role="columnheader" aria-sort={ariaSort('venue')} onDoubleClick={()=>handleHeaderSort('venue')} style={{cursor:'pointer'}} title="Double-click to sort">Venue {sortIndicator(listSortKey==='venue')}</th>
                      <th className="el-col-net" role="columnheader" aria-sort={ariaSort('netAmount')} onDoubleClick={()=>handleHeaderSort('netAmount')} style={{cursor:'pointer'}} title="Double-click to sort by customer total">Total {sortIndicator(listSortKey==='netAmount')}</th>
                      <th className="el-col-items" role="columnheader" aria-sort={ariaSort('items')} onDoubleClick={()=>handleHeaderSort('items')} style={{cursor:'pointer'}} title="Double-click to sort">Qty {sortIndicator(listSortKey==='items')}</th>
                      <th className="el-col-customer" role="columnheader" aria-sort={ariaSort('customer')} onDoubleClick={()=>handleHeaderSort('customer')} style={{cursor:'pointer'}} title="Double-click to sort">Customer {sortIndicator(listSortKey==='customer')}</th>
                      <th className="el-col-pm" role="columnheader" aria-sort={ariaSort('pm')} onDoubleClick={()=>handleHeaderSort('pm')} style={{cursor:'pointer'}} title="Double-click to sort">PM {sortIndicator(listSortKey==='pm')}</th>
                      <th className="el-col-sp" role="columnheader" aria-sort={ariaSort('sp')} onDoubleClick={()=>handleHeaderSort('sp')} style={{cursor:'pointer'}} title="Double-click to sort">SP {sortIndicator(listSortKey==='sp')}</th>
                      {availabilityMode === 'icon' && <th className="el-col-avail-icon" style={{ width: '18px' }} aria-label="Availability" />}
                      {(isManager || isAdmin) && <th className="el-col-actions">Actions</th>}
                    </tr>
                  </LibraryTableHead>
                  <tbody>
                    {sortedDisplayEvents.map(ev => {
                      const startDisplay = formatDateOnly(eventStartDateValue(ev));
                      const endDisplay = formatDateOnly(eventEndDateValue(ev));
                      const statusSummary = orderStatusSummary(ev);
                      const avail = availabilityMap.get(ev.id);
                      const ratio = avail?.ratio;
                      const availStatus = availabilityStatusFromRatio(ratio, avail?.returnWarning);
                      const availTip = availabilityTooltip(ratio, avail?.returnWarning, avail?.timeError);
                      const statuses = extractEventOrderStatuses(ev);
                      const listStageInfo = deriveOrderStage(statuses, orderStageLookup);
                      const listNextStatus = (() => {
                        const idx = orderedStatuses.findIndex(s => s.status === (listStageInfo?.status ?? ''));
                        return idx >= 0 && idx < orderedStatuses.length - 1 ? orderedStatuses[idx + 1]?.status ?? null : null;
                      })();
                      const hasAvailabilityList = avail !== undefined;
                      const rowGlowClassList = hasAvailabilityList && availabilityMode === 'glow' ? `event-row--avail-${availStatus}` : '';
                      const rowBarClassList = hasAvailabilityList && availabilityMode === 'bar' && availStatus !== 'unknown' ? `event-row--bar event-row--bar-${availStatus}` : '';
                      const barText = shortageBarText(avail);
                      return (
                        <tr key={ev.id} className={`${rowGlowClassList} ${rowBarClassList}`.trim()} title={hasAvailabilityList ? availTip : undefined} data-preview-id={ListPreviewPanel ? ev.id : undefined}>
                          <td data-label="Stage" className="truncate" title={statusSummary}>
                            {statusSummary} <EventFlags row={overdueMap.get(ev.id) ?? null} compact />
                            {showPeriodBadge(ev.id)}
                            {availabilityMode === 'bar' && barText && <span className={`event-row__bar-label event-row__bar-label--${availStatus}`}>{barText}</span>}
                          </td>
                          <td data-label="R2 #"><Link to={`/events/${ev.id}`}>{ev.event_code || ev.r2_contract_iid || ''}</Link></td>
                          <td data-label="Name" className="truncate" title={eventDisplayName(ev.name, ev.customer_pickup)}><Link to={`/events/${ev.id}`}>{eventDisplayName(ev.name, ev.customer_pickup) || 'Untitled Event'}</Link></td>
                          <td data-label="Start">{startDisplay}</td>
                          <td data-label="End">{endDisplay}</td>
                          <td data-label="Venue" className="truncate" title={primaryVenueName(ev)||''}>{primaryVenueName(ev) || ''}</td>
                          <td data-label="Total">{formatEventAmount(eventCustomerTotalAmount(ev))}</td>
                          <td data-label="Qty">{(ev.item_count ?? 0) > 0 ? ev.item_count : ''}</td>
                          <td data-label="Customer" className="truncate" title={ev.client_display_name ?? ev.client_business ?? ''}>{ev.client_display_name ?? ev.client_business ?? ''}</td>
                          <td data-label="PM" className="truncate" title={ev.project_manager_display_name ?? ''}>{ev.project_manager_display_name ?? '—'}</td>
                          <td data-label="SP" className="truncate" title={salesManagerDisplayName(ev) ?? ''}>{salesManagerDisplayName(ev) ?? '—'}</td>
                          {availabilityMode === 'icon' && (
                            <td data-label="Availability" className="el-col-avail-icon" style={{ width: '18px', textAlign: 'center', padding: '0 2px' }}>
                              {hasAvailabilityList && availStatus !== 'unknown' && <span className={`avail-icon avail-icon--${availStatus}`} title={availTip} />}
                            </td>
                          )}
                          {(isManager || isAdmin) && (
                            <td data-label="Actions">
                              <EventActionsDropdown
                                event={ev}
                                currentOrderStatus={listStageInfo?.status ?? null}
                                nextOrderStatus={listNextStatus}
                                orderedStatuses={orderedStatuses}
                                onDelete={handleDeleteEvent}
                                onStageUpdated={handleStageUpdated}
                                stopPropagation={false}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                renderGroupedSections()
              )}
              {ListPreviewPanel ? (
                <LayoutFeature name="listPreview">
                  <ListPreviewPanel listRef={listPreviewRef} getPreview={getEventPreview} label="Event preview" />
                </LayoutFeature>
              ) : null}
            </div>
          )
        )}

        {/* Pagination: only for paged non-calendar views. Calendar uses its own date navigation.
            Phones page with LibraryPager, last on the page (below). */}
        {totalCount > 0 && viewMode !== 'calendar' && !isPhone && (
          <nav aria-label="Event result pages" className="inventory-pagination" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="text-sm fg-muted">Events per page:</span>
              <select
                aria-label="Events per page"
                className="control xs"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ minWidth: '80px' }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="text-sm fg-muted">
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  type="button"
                  className="btn xs"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || refreshing}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn xs"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages || refreshing}
                >
                  Next
                </button>
              </div>
            </div>
          </nav>
        )}
        {totalCount > 0 && groupBy !== 'none' && viewMode !== 'calendar' && (
          <div className="text-sm fg-muted" style={{ marginTop: '1rem' }}>
            Events are grouped within this page. Use Previous and Next to see all matching events.
          </div>
        )}
        {availabilityMode !== 'off' && (
          <div className="avail-legend" aria-label="Availability legend">
            <span className="avail-legend__title text-xxs fg-muted">Availability</span>
            <span className="avail-legend__item"><span className="avail-legend__dot avail-legend__dot--green" />No shortages</span>
            <span className="avail-legend__item"><span className="avail-legend__dot avail-legend__dot--yellow" />Shortages or return risk</span>
            <span className="avail-legend__item"><span className="avail-legend__dot avail-legend__dot--red" />50%+ shortages</span>
          </div>
        )}
        {isPhone && viewMode !== 'calendar' ? (
          <LibraryPager
            page={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onPageChange={goToPhonePage}
            ariaLabel="Event result pages"
          />
        ) : null}
      </div>
      {showWizard && (
          <CreateEventWizard
            onCreated={onCreated}
            onCancel={() => {
              if (priceCheckDraftKeyFromUrl) {
                removePriceCheckEventDraft(priceCheckDraftKeyFromUrl);
              }
              setShowWizard(false);
            }}
            initialVenueId={initialVenueIdFromUrl}
            initialPriceCheckDraft={initialPriceCheckDraftFromUrl}
          />
      )}
      {showLeaderboard && (
        <EventLeaderboardModal
          rows={dashboardCounts?.byLocation ?? []}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
      {beoData && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setBeoData(null)}>
          <BEOImportReview
            packet={beoData}
            onCancel={() => setBeoData(null)}
            onComplete={() => setBeoData(null)}
          />
        </div>
      )}
      {showBeoUploadModal && (
        <BEOUploadModal
          onClose={() => setShowBeoUploadModal(false)}
          onJobStarted={(jobId, fileName) => addBeoJob(jobId, fileName)}
        />
      )}
      {eventsGuide.open && eventsGuide.activeStep ? (
        <GuideTourOverlay
          activeStep={eventsGuide.activeStep}
          ariaLabel="Events library guided tour"
          isFinalStep={eventsGuide.isFinalStep}
          onClose={eventsGuide.close}
          onNext={eventsGuide.next}
          onPrev={eventsGuide.prev}
          spotlightRect={eventsGuide.spotlightRect}
          stepCountLabel={eventsGuide.stepCountLabel}
          stepIndex={eventsGuide.stepIndex}
        />
      ) : null}
    </div>
  );
}
